"use client"

// ─────────────────────────────────────────────────────────────────────────
// RATED billing flow (FUNCTIONAL, feature-flagged) — full prototype parity.
//
// Wires every paid/free billing screen from panda_billing_states.html to the
// rated-billing endpoints. Pricing is per-plan (lib/billing/credit-rates.ts).
//
//   Free:      Out of credits → Choose a plan        → POST /api/billing/subscribe-rated
//   Add hub:   Upgrade / Auto top-up / Buy once
//     Buy      → POST /api/credits/topup-rated        (Checkout)
//     Upgrade  → POST /api/billing/upgrade-rated      (Checkout)
//     Auto     → PUT  /api/credits/auto-topup-rated   (settings)
//   Cancel and downgrade are DISTINCT, direct flows (PANDA-1411) — they never
//   cross, and neither is masked behind the other:
//   Cancel:    cancelConfirm → cancelConfirm2 → cancel-rated (intent:cancel) → Free
//   Downgrade: chooseDowngrade → downgradeConfirm → downgrade-rated (target paid tier)
//   The Lite retention offer is a Product placeholder (LITE_AVAILABLE=false),
//   reachable only via the cancel-confirm opt-in link — never an interstitial.
//
// Cancel schedules a move to Free; a downgrade re-prices to a lower PAID tier.
// Both are SCHEDULED at period end and reversible; they never appear as controls
// on the add-credits/upgrade screens (ticket requirement).
// ─────────────────────────────────────────────────────────────────────────

import * as React from "react"
import { XIcon, CheckIcon, InfoIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { MODAL_SCROLL_BODY } from "@/components/ui/modal-surface"
import {
  MODAL_ACCENT_PILL,
  MODAL_ACCENT_RING,
} from "@/components/ui/modal-accents"
import { cn } from "@/lib/utils"
import { redirectToBilling } from "@/lib/intentional-navigation"
import { isCustomCheckoutEnabled, customCheckoutHref } from "@/lib/billing/checkout-mode"
import { isWhop } from "@/lib/payments/provider"
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store"
import {
  trackTrialAddCreditsClicked,
  trackPricingPageViewed,
  trackPricingToggleSwitched,
  trackPlanCtaClicked,
  trackUpgradeCtaClicked,
  trackCheckoutStarted,
  type PricingSurface,
} from "@/lib/analytics"
import { useBillingCadence } from "@/lib/billing/cadence-preference"
import {
  coerceUpgradeCopyVariant,
  showsUpgradeCopyVariant,
  type UpgradeCopyVariant,
} from "@/lib/billing/upgrade-copy-experiment"
import { PRO_BENEFITS_VARIANT } from "@/lib/billing/pro-benefits"
import {
  RATED_PLANS,
  RATED_AUTO_TRIGGERS,
  isRatedPaidPlan,
  monthlyCents,
  annualCents,
  perMonthCents,
  topupCents,
  autoLimitOptions,
  planTotalOptions,
  upgradeTotalOptions,
  resolveUpgradeInitialTotal,
  nextUpgradeTotal,
  lowestPlanTotal,
  defaultTopupAmount,
  isValidPlanTotal,
  isValidTopupAmount,
  type RatedPlan,
  type RatedCadence,
} from "@/lib/billing/credit-rates"
import { ANNUAL_MONTHS_CHARGED } from "@/lib/pricing/credit-tiers"
import {
  PAID_PLAN_IDS,
  lowerPaidPlanIds,
  type PaidPlanId,
  type PlanId,
} from "@/lib/plans"
import { completedOnSavedCard } from "@/lib/billing/direct-charge-client"
import {
  isDomainUpgradeIntent,
  markDomainUpgradeRedirect,
} from "@/lib/domains/domain-upgrade-return"
import { stashCheckoutIntent } from "@/lib/billing/checkout-intent-stash"
import { queuePurchaseConfirmation } from "@/components/billing/purchase-confirmation-store"
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache"
import { TRIAL_CONVERT_EVENT } from "@/lib/trial/upgrade-entry-client"

export type Screen =
  | "loading"
  | "pausedFree"
  | "chooseplan"
  | "upgrade"
  | "auto"
  | "buy"
  | "cancelConfirm"
  | "cancelConfirm2"
  | "lite"
  | "chooseDowngrade"
  | "downgradeConfirm"
  | "scheduled"
  | "cancelScheduled"

export type RatedEntry = "auto" | "cancel" | "downgrade" | "chooseplan"

const usd = (cents: number) =>
  "$" +
  (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(cents / 100) ? 0 : 2,
    maximumFractionDigits: 2,
  })
const fmtN = (n: number) => n.toLocaleString("en-US")
// Sentence-case a plan id ("pro" → "Pro") for display. Plan ids are lowercase.
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "the end of your cycle"

// Lite save offer — PLACEHOLDER copy/pricing pending Product.
const LITE = {
  price: 5,
  off: "82% off",
  keeps: [
    "50 daily credits",
    "Keep custom domains",
    "No Panda badge",
    "Priority build queue",
  ],
}
// Whether Lite can actually be switched to. There is no Lite plan in the plan
// model (lib/plans.ts), no Stripe price, and no switch endpoint yet, so this is
// false: the retention offer's primary CTA renders DISABLED with customer-facing
// "not available yet" copy and the re-entry link on the cancel screen is hidden.
// When Lite ships, derive this from the real product config / backend response
// (never internal rollout status) and wire LiteScreen's onSwitchLite to the real
// plan-change request — mirroring the cancel/downgrade lifecycle() calls, with
// loading/disabled/success/failure states and only customer-facing copy.
const LITE_AVAILABLE = false

// Outcome-led plan benefits for the upgrade / compare cards, using the design's
// "Everything in X, plus:" framing (what you can DO, not feature bullets).
// Adapted from PRICING_TIERS (lib/pricing/credit-tiers.ts). PLACEHOLDER copy for
// Grow / Scale pending Product sign-off (illustrative in the proposal).
// The entry paid tier's bullets, shared by `pro` and its `starter` alias.
//
// The first bullet is the one that was missing, and its absence broke the gate
// funnel at its last step: four of the six gated capabilities (customer accounts,
// saved data, notifications, a permanent link) are the ones users actually hit
// mid-build, and this card — the screen every one of those gates opens — listed
// none of them. Someone pressed "Upgrade to save data for real" and arrived at a
// plan whose bullets were domains, branding, rollover and top-ups. The wall's
// heading carried the capability through (getFeatureBlockCopy) and then the card
// under it quietly changed the subject.
//
// Every claim is a flag in the plan catalog: customerAccounts, persistentData,
// notifications, acceptPayments, customDomain, removeBranding are all true for
// every paid tier (lib/plans.ts).
const ENTRY_PAID_BENEFITS = {
  plus: "Everything in Free, plus:",
  items: [
    "Real customer accounts and saved data that lasts",
    "Take real payments with Stripe",
    "Publish to your own custom domain",
    "Remove Panda branding",
    // The two credit bullets merged into one so the capabilities above could be
    // added without pushing the card to six lines. Both facts survive: rollover
    // and on-demand top-ups (free has neither).
    "Unused credits roll over, and top up any time",
  ],
}

const PLAN_BENEFITS: Record<PaidPlanId, { plus: string; items: string[] }> = {
  // Legacy alias of the Pro (×1) tier — same outcomes as Pro.
  starter: ENTRY_PAID_BENEFITS,
  pro: ENTRY_PAID_BENEFITS,
  grow: {
    plus: "Everything in Pro, plus:",
    items: [
      "Get customers on autopilot with Grow Mode",
      "Reach them on every marketing channel",
      "Skip the queue with priority builds",
    ],
  },
  scale: {
    plus: "Everything in Grow, plus:",
    items: [
      "Run a team of AI businesses",
      "Priority support when you need it",
      "Advanced team controls and permissions",
    ],
  },
}

// Safe fallback headline/sub for the mid-session out-of-credits screen when the
// personalisation call (/api/ai/paywall-headline) has no usable context or has
// not resolved yet. Mirrors the FALLBACK block in that endpoint's prompt.
const PAYWALL_FALLBACK = {
  headline: "Build more with Pro",
  // Balance-neutral: #2 now also shows when a positive-but-below-minimum balance
  // is blocked by the estimate checker, so this must not claim "you're out" over
  // a still-visible credit count. "Hit your credit limit" is true at 0 AND low.
  subheading:
    "You've hit your credit limit. Upgrade to keep building without interruptions.",
}

// Subheading for the DAILY-cap variant of #2: today's free credits are spent but
// the pool still holds credits, so this names the daily limit (accurate next to
// a positive balance chip) and pitches Pro's no-daily-limit benefit — never
// "out of credits". The goal headline above it stays personalised.
const DAILY_CAP_SUBHEADING =
  "You've used today's free credits. Upgrade to Pro to keep building now, with no daily limit."

// Max time to wait for the personalised headline before revealing the fallback —
// a safety net so the loading placeholder never sticks on a hung/slow request.
// The endpoint itself caps generation at ~6s, so this only trips on a network
// stall; in the normal case the fetch settles first and this timer is cleared.
const PERSONALIZE_MAX_WAIT_MS = 8000

interface Status {
  plan: string
  credits_remaining: number
  has_card: boolean
  cancel_scheduled: boolean
  period_end: string | null
  /** The account's custom monthly credit allowance (the upgraded total), or
   *  null when on the plan's base allowance. Cancel screen shows this dropping
   *  to 0; null falls back to the plan's baseCredits (its included allowance). */
  custom_monthly_credits: number | null
  /** The subscription is still a live $1 trial (Stripe status 'trialing'), so
   *  the scheduled-cancellation screen shows "trial cancelled" copy instead of
   *  the paid "Subscription ends / Pro → Free" wording (PANDA trial-copy). */
  is_trial: boolean
  /** Billing interval of the live subscription. The credit screens never offer
   *  a cadence choice (raising credits doesn't change how often you're billed),
   *  but they must label an annual subscriber's price honestly. */
  cadence: RatedCadence
  /** Whether `cadence` is the subscriber's REAL interval or still the seeded
   *  guess. No host passes a cadence today (they'd have to call
   *  /api/billing/rated-status to know it), so a seeded open always guessed
   *  "monthly" and an annual subscriber watched the monthly dropdown and
   *  "Update my plan" CTA render for 3–4s before the fetch replaced them with
   *  the annual/top-up screen (PANDA-2290). Screens whose CONTROLS depend on
   *  the interval must wait for this; screens that merely LABEL a price may
   *  keep using the seed. */
  cadence_resolved: boolean
  /** Upgrade-copy A/B arm, resolved server-side and shared with the $1-trial
   *  modal so one user never straddles both arms
   *  (lib/billing/upgrade-copy-experiment.ts). */
  copy_variant: UpgradeCopyVariant
}

/** Partial account data a caller already knows, used to seed the flow so it
 *  renders the right screen instantly instead of flashing a loading state.
 *  Only `plan` is required; the rest fill in from the background refresh. */
export interface RatedInitialStatus {
  plan: string
  credits_remaining?: number
  has_card?: boolean
  cancel_scheduled?: boolean
  period_end?: string | null
  custom_monthly_credits?: number | null
  is_trial?: boolean
  cadence?: RatedCadence
  /** Upgrade-copy A/B arm, if the host already knows it (pages read it from
   *  /api/credits on mount, well before any modal opens). Seeding it is what
   *  makes the modal paint the correct arm on its FIRST frame — omit it and the
   *  arm only arrives with this flow's own status read, so the user sees the
   *  control copy and then watches it swap. Omitted → "control" until that read
   *  lands, which is the old behaviour. */
  copy_variant?: UpgradeCopyVariant
}

/** Context for the mid-session personalised headline (#2 — out of credits).
 *  When `blockedPrompt` is present the flow calls /api/ai/paywall-headline to
 *  generate an outcome-laddered headline + subheading; otherwise the safe
 *  fallback copy stands. */
export interface RatedPersonalization {
  projectId?: string | null
  /** The prompt that could not run because credits hit 0. */
  blockedPrompt?: string
  /** The user's recent prompts (context for the outcome ladder). */
  recentPrompts?: string[]
  /** Short description of what they are building. */
  projectSummary?: string
  /** Set when the block was the free-tier DAILY cap (today's allowance is spent)
   *  rather than an empty/low pool. The pool still holds credits, so the "Build
   *  paused" screen keeps the personalised (goal) headline but swaps in a
   *  daily-accurate subheading — never an "out of credits" line that would
   *  contradict the still-positive balance chip. */
  dailyCap?: { resetsAt?: string | null }
}

export interface RatedChoosePlanCopy {
  headline: string
  subheading: string
}

/** Pick the entry screen from account status — pure so it can seed the initial
 *  render synchronously AND resolve again after the background refresh. */
function entryScreen(s: Status, entry?: RatedEntry): Screen {
  if (s.cancel_scheduled) return "cancelScheduled"
  if (!isRatedPaidPlan(s.plan)) return "chooseplan"
  // Cancel and downgrade are DISTINCT, direct flows (PANDA-1411):
  //   • Cancel   → straight to the cancellation confirm (the Lite retention
  //     offer, a Product placeholder, is reachable only via the confirm
  //     screen's opt-in link, gated by LITE_AVAILABLE — never an interstitial
  //     that makes "Cancel subscription" feel indirect).
  //   • Downgrade → straight to the lower-paid-tier picker (Scale → Grow/Pro,
  //     Grow → Pro) — never the cancel-to-Free path.
  if (entry === "cancel") return "cancelConfirm"
  if (entry === "downgrade") return "chooseDowngrade"
  if (entry === "auto") return "auto"
  // Add-more-credits now opens directly on the single "Add more credits" card
  // (the redesigned upgrade screen), with one-off top-ups demoted to a link
  // there. The old 3-way hub is retired.
  return "upgrade"
}

export function RatedCreditsFlow({
  entry,
  startScreen,
  onClose,
  returnTo,
  initialStatus,
  source,
  intent,
  initialPlan,
  initialCredits,
  insufficientCredits,
  personalization,
  choosePlanCopy,
  fullScreenOnMobile,
  onCopyVariantResolved,
}: {
  /** Reports the resolved copy A/B arm once the status read settles (including
   *  on failure, as "control"). The modal host defers its upgrade_modal_shown
   *  impression until this fires, so every impression carries the arm that
   *  actually rendered — without it the test's primary metric
   *  (shown → cta_clicked) can't be split A/B at all. */
  onCopyVariantResolved?: (v: UpgradeCopyVariant) => void
  entry?: RatedEntry
  /** Review harness override — jump straight to a specific screen. */
  startScreen?: Screen
  onClose?: () => void
  /** Where Stripe Checkout returns after a top-up/upgrade/subscribe. The host
   *  page must run the reconcile-on-return effect for its query params. When
   *  omitted, checkout returns to the CURRENT page (where the user opened the
   *  flow) so a purchase never bounces them off to a different screen. */
  returnTo?: string
  /** Known plan/credits from the caller — seeds the first render so there's no
   *  loading flash. The background fetch still runs to refresh + fill the rest. */
  initialStatus?: RatedInitialStatus
  /** Entry-point context (e.g. "stripe_button") forwarded onto every checkout
   *  this flow starts — so the origin of a Pro-gated upgrade is attributable. */
  source?: string
  /** The gated intent the user is trying to reach (e.g. "connect_stripe"),
   *  forwarded onto checkout alongside `source`. */
  intent?: string
  /** Which plan the Choose-a-plan screen recommends (ring + primary CTA).
   *  Defaults to "pro" (the entry tier for a Pro-gated action). Every plan is
   *  shown regardless — this only sets the emphasis. */
  initialPlan?: PaidPlanId
  /** Credit amount to open the picker on, restoring what the user had selected
   *  before an abandoned Stripe round-trip (lib/billing/checkout-intent-stash).
   *  Each screen ignores it unless it's a valid rung on its own ladder, so a
   *  stale or cross-plan amount can never preselect something unpurchasable. */
  initialCredits?: number
  /** Positive-but-below-cost balance (PANDA-1367): when the free user opened
   *  this from an insufficient-balance block, name the shortfall above the plan
   *  picker so it never reads as "you're out" over a still-positive counter. */
  insufficientCredits?: { available: number; required?: number } | null
  /** Context for the mid-session personalised headline (#2). When a
   *  `blockedPrompt` is present, the flow calls /api/ai/paywall-headline to
   *  generate the out-of-credits headline + subheading (outcome-laddered); a
   *  safe fallback is used when context is thin or the call fails. */
  personalization?: RatedPersonalization | null
  /** Static context for non-credit gates that still lead to Choose a plan,
   *  such as the project limit. Unlike `personalization`, this is deterministic
   *  copy and never calls the headline-generation endpoint. */
  choosePlanCopy?: RatedChoosePlanCopy
  /** When true, the card fills the whole screen on phones (edge-to-edge, no
   *  rounded corners) and only becomes a centered card at ≥sm. Set by the modal
   *  host so the out-of-credits flow reads as a full page on mobile instead of a
   *  cramped pop-up trapped behind the chat panel; the standalone
   *  /billing/credits page leaves it off and keeps its centered card. */
  fullScreenOnMobile?: boolean
}) {
  const seeded: Status | null = initialStatus
    ? {
        plan: initialStatus.plan,
        credits_remaining: initialStatus.credits_remaining ?? 0,
        has_card: initialStatus.has_card ?? false,
        cancel_scheduled: initialStatus.cancel_scheduled ?? false,
        period_end: initialStatus.period_end ?? null,
        custom_monthly_credits: initialStatus.custom_monthly_credits ?? null,
        is_trial: initialStatus.is_trial ?? false,
        // Hosts don't know the cadence; the background refresh fills it in. Seed
        // monthly — the overwhelmingly common case — so the first frame never
        // mislabels a monthly subscriber as annual.
        //
        // The seed is a GUESS, and `cadence_resolved` says so. It stays usable
        // for labelling, but no screen may render interval-dependent CONTROLS
        // from it — that guess is what showed annual subscribers a monthly
        // upgrade CTA for 3–4 seconds (PANDA-2290).
        cadence: initialStatus.cadence ?? "monthly",
        cadence_resolved: initialStatus.cadence != null,
        // Seeded from the host when it knows the arm (from /api/credits on page
        // mount), so the first frame is already correct and nothing swaps. A
        // host that doesn't know it falls back to "control" — the safe baseline
        // — and the background status read corrects it.
        copy_variant: initialStatus.copy_variant ?? "control",
      }
    : null
  // Captured once: whether we opened with a seed. When true, the background
  // refresh only updates data — it never shows an error, and it re-picks the
  // screen ONLY while the user is still on the seed-resolved entry screen
  // (see load()), so a stale seed (e.g. cancel_scheduled missing) gets
  // corrected without ever yanking the user off a screen they navigated to.
  const wasSeededRef = React.useRef(seeded !== null)
  // A host-seeded arm is ALREADY server-resolved (it came from /api/credits), so
  // report it immediately rather than making the impression wait on this flow's
  // own status read. Without this the upgrade_modal_shown event is delayed by a
  // round trip — and a user who dismisses inside it produces no impression at
  // all, which is precisely the fast-dismissal population this test is about.
  // The host's handler is idempotent (it fires the impression once), so the
  // later report from load() only refreshes the value.
  const seededArm = initialStatus?.copy_variant
  React.useEffect(() => {
    if (seededArm) onCopyVariantResolved?.(seededArm)
    // Fire once for the seeded value; load() reports the authoritative one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededArm])
  // The screen the seed resolved to — the "user hasn't navigated" reference.
  const initialScreenRef = React.useRef<Screen | null>(
    seeded ? (startScreen ?? entryScreen(seeded, entry)) : null,
  )

  const [status, setStatus] = React.useState<Status | null>(seeded)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [screen, setScreen] = React.useState<Screen>(
    seeded ? (startScreen ?? entryScreen(seeded, entry)) : "loading",
  )
  // Current screen, readable inside the async load() without a stale closure.
  // Synced in an effect (not during render, per react-hooks/refs); load() only
  // reads it after its fetch resolves, well past the first effect pass.
  const screenRef = React.useRef(screen)
  React.useEffect(() => {
    screenRef.current = screen
  }, [screen])
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [autoSaved, setAutoSaved] = React.useState(false)
  // The lower paid tier the user picked to downgrade to (Grow/Pro), or null
  // until they choose one on the downgrade picker.
  const [downgradeTarget, setDowngradeTarget] =
    React.useState<PaidPlanId | null>(null)

  // Personalised mid-session headline/sub (#2). To avoid the "flutter" of showing
  // the fallback headline first and then swapping in the LLM copy, we start in a
  // PENDING state (null → loading placeholder) whenever a blocked prompt will
  // trigger generation, and reveal the headline exactly ONCE: the dynamic copy if
  // it arrives, else the safe fallback (error, invalid, or a hard timeout). With
  // no blocked prompt there's nothing to wait for, so the fallback shows at once.
  const pBlocked = personalization?.blockedPrompt
  const pProject = personalization?.projectId
  const willPersonalize = Boolean(pBlocked && pBlocked.trim())
  const [personalized, setPersonalized] = React.useState<
    { headline: string; subheading: string } | null
  >(willPersonalize ? null : PAYWALL_FALLBACK)
  React.useEffect(() => {
    if (!pBlocked) return
    // First writer wins: whichever settles first (the fetch or the safety
    // timeout) reveals the headline; the other is ignored — so a late arrival
    // can never swap the copy after it's already shown (that swap IS the flutter).
    let settled = false
    const settle = (value: { headline: string; subheading: string }) => {
      if (!settled) {
        settled = true
        setPersonalized(value)
      }
    }
    const timer = setTimeout(
      () => settle(PAYWALL_FALLBACK),
      PERSONALIZE_MAX_WAIT_MS,
    )
    ;(async () => {
      try {
        const res = await fetch("/api/ai/paywall-headline", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            blocked_prompt: pBlocked,
            project_summary: personalization?.projectSummary ?? "",
            recent_prompts: personalization?.recentPrompts ?? [],
            projectId: pProject ?? null,
          }),
        })
        if (!res.ok) return settle(PAYWALL_FALLBACK)
        const json = await res.json().catch(() => null)
        if (
          json &&
          typeof json.headline === "string" &&
          typeof json.subheading === "string" &&
          json.headline.trim() &&
          json.subheading.trim()
        ) {
          settle({
            headline: json.headline.trim(),
            subheading: json.subheading.trim(),
          })
        } else {
          settle(PAYWALL_FALLBACK)
        }
      } catch {
        settle(PAYWALL_FALLBACK)
      }
    })()
    return () => {
      // Unmount: block any pending settle so we never setState after unmount.
      settled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pBlocked, pProject])

  // Bumped to force the status effect below to re-fetch — used when the page is
  // restored from the back-forward cache after a Stripe round-trip (see the
  // bfcache handler), so a completed or abandoned checkout is re-confirmed.
  const [reloadNonce, setReloadNonce] = React.useState(0)

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      // Whatever happens below, the host is told which arm rendered — a failed
      // status read still resolves, to "control". The upgrade_modal_shown
      // impression waits on this, so a silent failure here would drop the
      // impression entirely rather than merely mislabel it.
      let resolvedArm: UpgradeCopyVariant = "control"
      try {
        const [creditsRes, statusRes] = await Promise.all([
          fetch("/api/credits").then((r) => r.json()),
          fetch("/api/billing/rated-status").then((r) => r.json()),
        ])
        if (cancelled) return
        const s: Status = {
          plan: statusRes.plan ?? creditsRes.plan ?? "free",
          credits_remaining: creditsRes.credits_remaining ?? 0,
          has_card: Boolean(creditsRes.has_card),
          cancel_scheduled: Boolean(statusRes.cancel_scheduled),
          period_end: statusRes.period_end ?? null,
          custom_monthly_credits: statusRes.custom_monthly_credits ?? null,
          is_trial: Boolean(statusRes.is_trial),
          cadence: statusRes.cadence === "annual" ? "annual" : "monthly",
          // This read IS the authority, however it coerced. Anything gated on
          // resolution unblocks here and never earlier.
          cadence_resolved: true,
          // Unknown/missing coerces to "control": a seeded-then-failed status
          // must fall back to today's copy, never drift into the test arm.
          copy_variant: coerceUpgradeCopyVariant(statusRes.copy_variant),
        }
        resolvedArm = s.copy_variant
        setStatus(s)
        // A seeded open already resolved its screen instantly; only pick the
        // screen from the fetch when we started from the loading state.
        if (!wasSeededRef.current) {
          setScreen(startScreen ?? entryScreen(s, entry))
        } else {
          // Seeded open: the fresh status may contradict the seed (e.g. the
          // caller didn't know a cancellation was scheduled). Re-pick ONLY if
          // the user is still sitting on the seed-resolved entry screen — a
          // user who has navigated is never yanked.
          const next = startScreen ?? entryScreen(s, entry)
          if (
            screenRef.current === initialScreenRef.current &&
            next !== screenRef.current
          ) {
            setScreen(next)
          }
        }
      } catch {
        // A seed keeps the flow fully usable (prices come from client
        // constants), so a failed refresh isn't fatal — only surface the error
        // when there was nothing to fall back on.
        if (!cancelled && !wasSeededRef.current) setLoadError("Couldn't load your plan.")
      } finally {
        if (!cancelled) onCopyVariantResolved?.(resolvedArm)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // onCopyVariantResolved is intentionally NOT a dependency: hosts pass an
    // inline callback, and adding it would re-run the whole status load on
    // every host render. It is only ever called once per load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, startScreen, reloadNonce])

  // Returning from Stripe Checkout via the browser back button restores this
  // page from the back-forward cache with React state intact — including the
  // `busy` flag set right before the redirect. That would pin every CTA on
  // "Starting…" with no way to retry (PANDA-1461). On a bfcache restore, clear
  // `busy` so the buttons are interactive again, and bump reloadNonce to
  // re-confirm the account: a completed payment then reflects the upgraded
  // state, while an abandoned one leaves the plan picker fully interactive.
  useResetOnBfcache(
    React.useCallback(() => {
      setBusy(false)
      setReloadNonce((n) => n + 1)
    }, []),
  )

  if (loadError) return <Shell onClose={onClose} fullScreenOnMobile={fullScreenOnMobile}><Notice>{loadError}</Notice></Shell>
  if (!status || screen === "loading")
    // Silent skeleton — no "Loading…" text — for the rare un-seeded open (the
    // standalone page). Modal callers seed initialStatus, so they skip this
    // entirely and render their content on the first frame.
    return (
      <Shell onClose={onClose} fullScreenOnMobile={fullScreenOnMobile}>
        <div className="space-y-4" aria-busy="true" aria-label="Loading">
          <div className="h-11 w-full animate-pulse rounded-xl bg-muted/40" />
          <div className="mt-2 h-8 w-2/3 animate-pulse rounded-lg bg-muted/40" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-muted/30" />
          <div className="mt-4 h-12 w-full animate-pulse rounded-xl bg-muted/40" />
        </div>
      </Shell>
    )

  // ── $1 trial: this flow has no working outcome, so don't show a buy screen ──
  //
  // A trial row is plan='pro', so entryScreen() above resolves a trialing user
  // to "upgrade" — the Add-more-credits screen — and every button on it is then
  // refused inline (TRIAL_LOCKED_FLOW_MSG below). That is the reported bug: a
  // purchase screen that cannot purchase.
  //
  // The routing fix (lib/trial/upgrade-entry-client.ts) means the ~22 upgrade
  // entry points never open this flow for a trialing user any more. This is the
  // backstop for the paths that do NOT go through that router and so cannot be
  // fixed there: the /billing/credits URL typed directly, a resumed checkout
  // intent, and the deploy-fix toast's own button.
  //
  // Scoped to the BUY screens on purpose. "Cancel trial" is a real, working
  // trial flow reached from Settings → Billing (openRated("cancel") →
  // "cancelConfirm"), and downgrade / scheduled / auto are plan management, not
  // purchases. Redirecting those would break working functionality to fix a
  // buying one, so they are left exactly as they are.
  const TRIAL_BUY_SCREENS: readonly Screen[] = [
    "upgrade",
    "buy",
    "chooseplan",
    "pausedFree",
  ]
  if (status.is_trial && TRIAL_BUY_SCREENS.includes(screen)) {
    return (
      <Shell onClose={onClose} fullScreenOnMobile={fullScreenOnMobile}>
        <div className="py-2 text-center">
          <p className="text-xs font-semibold tracking-wide text-emerald-600 uppercase dark:text-emerald-400">
            Free trial
          </p>
          <h2 className="mt-2 text-xl font-semibold text-foreground">
            Convert to Pro to add credits
          </h2>
          <p className="mx-auto mt-2 max-w-100 text-sm leading-relaxed text-muted-foreground">
            Buying credits and changing plans are paused during your free trial.
            Convert to Pro and your full monthly credits land right away.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <Button
              // Non-destructive primary inside a modal → "success", not the
              // default brand gradient (PANDA-1779, enforced by
              // tests/unit/components/modal-non-destructive-palette.test.ts).
              variant="success"
              className="w-full rounded-xl"
              onClick={() => {
                // Hand off to the ONE convert surface the whole app uses, rather
                // than growing a second conversion UI in here that would drift
                // from it. The host is mounted in the root layout, so it is
                // listening on every route this flow can appear on.
                window.dispatchEvent(
                  new CustomEvent(TRIAL_CONVERT_EVENT, {
                    detail: { entrypoint: "rated_credits_modal", entrySurface: "billing_settings" },
                  }),
                )
                onClose?.()
              }}
            >
              Convert to Pro now
            </Button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Not now
              </button>
            )}
          </div>
        </div>
      </Shell>
    )
  }

  // Shown when a $1-trial user reaches a buy/upgrade action on a surface that
  // isn't the (already-gated) Billing card — e.g. the in-build credit wall, the
  // nav chip, the usage card. The server also blocks these (trial_locked), but
  // stopping here shows a clear message instead of a failed request.
  //
  // Declared ABOVE the render's `return` on purpose. It used to sit beside the
  // action handlers below it, where the `const` is never reached — the component
  // has already returned — so the three handlers that read it threw a TDZ
  // ReferenceError instead of setting the message: a trial user's click did
  // nothing at all, with no error shown. The handlers are hoisted function
  // declarations, which is why only this binding was affected.
  const TRIAL_LOCKED_FLOW_MSG =
    "Purchases and plan changes are available once your free trial converts to Pro."

  const plan = isRatedPaidPlan(status.plan) ? (status.plan as PaidPlanId) : null
  const cfg = plan ? RATED_PLANS[plan] : null

  // Choose-a-plan is the one screen showing every plan at once, so it's the one
  // that needs the wider shell. Two paths reach it: the free-account entry
  // screen, and the fallback below for a free account that landed on a paid-only
  // screen. Derived once so the shell width and that fallback can't disagree.
  const showingChoosePlan =
    screen === "chooseplan" || (!plan && screen !== "pausedFree")

  // Only the cancel path reaches the Lite retention offer now — a downgrade
  // goes straight to the lower-paid-tier picker (PANDA-1411). So Lite's origin
  // is always "cancel" and its secondary CTA resumes the cancellation flow.
  const liteOrigin = "cancel" as const

  return (
    <Shell
      onClose={onClose}
      fullScreenOnMobile={fullScreenOnMobile}
      wide={showingChoosePlan}
    >
      {err && (
        <div className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive ring-1 ring-destructive/30">
          {err}
        </div>
      )}

      {screen === "pausedFree" && (
        <PausedFreeScreen
          // null personalized = still resolving → show a placeholder instead of
          // the fallback, so the headline reveals ONCE (no fallback→dynamic swap).
          loadingHeadline={personalized === null}
          headline={personalized?.headline ?? PAYWALL_FALLBACK.headline}
          // Daily-cap block keeps the personalised (goal) headline but uses a
          // daily-accurate subheading so it never reads as "out of credits" over
          // a still-positive pool balance. That sub is fixed (no wait) so it shows
          // immediately; the AI sub waits alongside the headline.
          loadingSub={personalized === null && !personalization?.dailyCap}
          subheading={
            personalization?.dailyCap
              ? DAILY_CAP_SUBHEADING
              : (personalized?.subheading ?? PAYWALL_FALLBACK.subheading)
          }
          busy={busy}
          currentPlan={status?.plan ?? "free"}
          initialCredits={initialCredits}
          copyVariant={status?.copy_variant ?? "control"}
          onDecline={onClose}
          onSubmit={(p, credits, cadence, surface) =>
            checkout(
              "/api/billing/subscribe-rated",
              { plan: p, credits, cadence },
              surface,
            )
          }
        />
      )}
      {screen === "chooseplan" && (
        <ChoosePlanScreen
          busy={busy}
          currentPlan={status?.plan ?? "free"}
          initialPlan={initialPlan}
          initialCredits={initialCredits}
          insufficientCredits={insufficientCredits}
          contextCopy={choosePlanCopy}
          copyVariant={status?.copy_variant ?? "control"}
          // Soft exit closes the flow the same way the X does — the host owns
          // what "close" means on its surface (reload, refetch, clear intent),
          // so reusing onClose keeps the decline on that one path.
          onDecline={onClose}
          onSubmit={(p, credits, cadence, surface) =>
            checkout(
              "/api/billing/subscribe-rated",
              { plan: p, credits, cadence },
              surface,
            )
          }
        />
      )}

      {plan && cfg && (
        <>
          {/* Add more credits (#3) — the entry screen for paid accounts; the
              old 3-way hub is retired. One-off top-ups reachable via its link. */}
          {screen === "upgrade" && (
            <UpgradeScreen
              plan={plan}
              // Open on the account's CURRENT subscribed allocation, never a
              // hardcoded base — so reopening after a 1,000→3,000 upgrade shows
              // 3,000 selected. custom_monthly_credits is the raw upgraded total
              // (null on a base plan → the screen falls back to the base).
              currentTotal={status.custom_monthly_credits}
              cadence={status.cadence}
              cadenceResolved={status.cadence_resolved}
              busy={busy}
              initialCredits={initialCredits}
              onBuyOnce={() => setScreen("buy")}
              onSubmit={(credits) =>
                checkout("/api/billing/upgrade-rated", { credits })
              }
            />
          )}
          {screen === "buy" && (
            <BuyScreen
              plan={plan}
              cfg={cfg}
              busy={busy}
              initialCredits={initialCredits}
              onBack={() => setScreen("upgrade")}
              onSubmit={buyWithAuto}
            />
          )}
          {screen === "auto" && (
            <AutoScreen
              plan={plan}
              cfg={cfg}
              hasCard={status.has_card}
              busy={busy}
              saved={autoSaved}
              onBack={() => {
                setAutoSaved(false)
                setScreen("upgrade")
              }}
              onSave={saveAuto}
            />
          )}
          {screen === "cancelConfirm" && (
            <CancelConfirmScreen
              plan={plan}
              monthlyCredits={status.custom_monthly_credits ?? RATED_PLANS[plan].baseCredits}
              periodEnd={status.period_end}
              isTrial={status.is_trial}
              busy={busy}
              onLite={() => setScreen("lite")}
              onKeep={onClose}
              // Cancelling is destructive and irreversible from here, so this
              // never cancels directly — it opens a second confirm step. The
              // actual cancel only fires from that screen's confirm button.
              // Clear any prior error so a failed attempt's banner doesn't
              // linger when navigating between the two cancel screens.
              onCancel={() => {
                setErr(null)
                setScreen("cancelConfirm2")
              }}
            />
          )}
          {screen === "cancelConfirm2" && (
            <CancelFinalConfirmScreen
              periodEnd={status.period_end}
              isTrial={status.is_trial}
              busy={busy}
              // "Keep my plan" here is the SAME action as on the first cancel
              // screen — close the flow with the subscription untouched — so
              // both steps resolve a "no" identically rather than one of them
              // dropping the user back a step. onClose is the host's own
              // close (it refreshes the billing overview).
              onKeep={onClose}
              onConfirm={() =>
                lifecycle("/api/billing/cancel-rated", { intent: "cancel" }, "close")
              }
            />
          )}
          {screen === "lite" && (
            <LiteScreen
              origin={liteOrigin}
              // Gate the primary CTA on real availability. While false it renders
              // disabled with "not available yet" copy — never an enabled action
              // and never the old internal rollout-status error. When Lite ships,
              // flip LITE_AVAILABLE and pass an `onSwitchLite` that runs the real
              // plan-change request (see LITE_AVAILABLE note).
              available={LITE_AVAILABLE}
              // Secondary CTA continues the cancellation flow (Lite is a
              // cancel-only retention offer now — downgrade never routes here).
              onSecondary={() => setScreen("cancelConfirm")}
            />
          )}
          {screen === "chooseDowngrade" && (
            <ChooseDowngradeScreen
              plan={plan}
              currentCredits={
                status.custom_monthly_credits ?? RATED_PLANS[plan].baseCredits
              }
              selected={downgradeTarget}
              onSelect={setDowngradeTarget}
              onKeep={onClose}
              onContinue={() => setScreen("downgradeConfirm")}
            />
          )}
          {screen === "downgradeConfirm" && downgradeTarget && (
            <DowngradeConfirmScreen
              fromPlan={plan}
              fromCredits={
                status.custom_monthly_credits ?? RATED_PLANS[plan].baseCredits
              }
              toPlan={downgradeTarget}
              periodEnd={status.period_end}
              busy={busy}
              onBack={() => setScreen("chooseDowngrade")}
              // Schedules the paid-tier downgrade (NOT a cancel-to-Free): the
              // subscription switches to the chosen lower plan next cycle. The
              // modal closes on success; the scheduled state renders inline on
              // the billing page.
              onConfirm={() =>
                lifecycle(
                  "/api/billing/downgrade-rated",
                  { plan: downgradeTarget },
                  "close",
                )
              }
            />
          )}
          {screen === "scheduled" && (
            <ScheduledBanner
              tone="warn"
              title={`Pending changes scheduled for ${fmtDate(status.period_end)}`}
              desc={`${cap(plan)} (${fmtN(status.custom_monthly_credits ?? RATED_PLANS[plan].baseCredits)} credits) → Free`}
              action="Cancel changes"
              busy={busy}
              onAction={() => lifecycle("/api/billing/resume-rated", {}, "upgrade")}
            />
          )}
        </>
      )}

      {plan && screen === "cancelScheduled" && (
        <ScheduledBanner
          tone="red"
          // A cancelled $1 trial reads as a trial cancellation, never a paid Pro
          // cancellation (PANDA trial-copy) — kept consistent with the inline
          // billing-page banner.
          title={
            status.is_trial
              ? `Trial ends ${fmtDate(status.period_end)}`
              : `Subscription ends ${fmtDate(status.period_end)}`
          }
          desc={
            status.is_trial
              ? `Trial → Free (trial cancelled)`
              : `${cap(plan)} → Free (subscription cancelled)`
          }
          action={status.is_trial ? "Resume trial" : "Resume plan"}
          busy={busy}
          onAction={() => lifecycle("/api/billing/resume-rated", {}, "upgrade")}
        />
      )}

      {/* Free account that reached a paid-only screen — via a forced
          startScreen, stale client state, or a direct screen invocation.
          One-off top-ups / management are paid-only, so route to choose-a-plan
          (upgrade) instead of offering or naming them anywhere. */}
      {showingChoosePlan && screen !== "chooseplan" && (
        <ChoosePlanScreen
          busy={busy}
          currentPlan={status?.plan ?? "free"}
          initialPlan={initialPlan}
          initialCredits={initialCredits}
          contextCopy={choosePlanCopy}
          copyVariant={status?.copy_variant ?? "control"}
          onDecline={onClose}
          onSubmit={(p, credits, cadence, surface) =>
            checkout(
              "/api/billing/subscribe-rated",
              { plan: p, credits, cadence },
              surface,
            )
          }
        />
      )}
    </Shell>
  )

  // ── actions ──
  async function checkout(
    url: string,
    body: Record<string, unknown>,
    /** Set only by the plan screens — top-ups have no cadence to report. */
    pricingSurface?: PricingSurface,
  ) {
    if (status?.is_trial) {
      trackTrialAddCreditsClicked({ entry_point: source, surface: "rated_flow" })
      setErr(TRIAL_LOCKED_FLOW_MSG)
      return
    }
    setErr(null)
    setBusy(true)
    // AFTER the trial gate and BEFORE the request: a blocked click is intent
    // (plan_cta_clicked already recorded it) but no checkout ever started, and
    // firing here covers the saved-card path that never reaches Stripe as well
    // as the redirect. Guarded on the plan fields so a top-up can't emit a
    // subscription event.
    if (
      pricingSurface &&
      typeof body.plan === "string" &&
      (body.cadence === "monthly" || body.cadence === "annual")
    ) {
      trackCheckoutStarted({
        plan: body.plan,
        cadence: body.cadence,
        surface: pricingSurface,
      })
    }
    // Payments-paused guard. This function is the single choke point for every
    // rated purchase — plan subscribe, raise-credits upgrade and top-up all
    // funnel through it — so one guard here covers the whole flow. Placed after
    // trackCheckoutStarted so a blocked click still counts as intent, and the
    // busy state is cleared because no navigation will follow.
    const isRatedTopup = url.includes("topup-rated")
    const isRaiseCredits = url.includes("upgrade-rated")
    const ratedCredits = typeof body.credits === "number" ? body.credits : null
    // The tier being bought (subscribe carries it in the body; top-up and
    // raise-credits act on the plan the account is already on).
    const ratedPlan =
      typeof body.plan === "string" && isRatedPaidPlan(body.plan)
        ? (body.plan as PaidPlanId)
        : plan
    const ratedCadence = body.cadence === "annual" ? "annual" : "monthly"
    // Price the pop-up from the SAME helpers the screens price with, so what a
    // blocked buyer is quoted matches what they were about to be charged.
    const pausedCents = (() => {
      if (!ratedPlan || !ratedCredits) return null
      return isRatedTopup
        ? topupCents(ratedPlan, ratedCredits)
        : perMonthCents(ratedPlan, ratedCredits, ratedCadence)
    })()
    const pausedPriceLabel =
      pausedCents === null
        ? null
        : `$${Math.round(pausedCents / 100)}${isRatedTopup ? "" : "/mo"}`
    if (
      interceptIfPaymentsPaused({
        kind: isRatedTopup ? "topup" : "upgrade",
        surface: `rated_flow_${isRatedTopup ? "topup" : isRaiseCredits ? "raise_credits" : "subscribe"}`,
        label: isRatedTopup
          ? ratedCredits
            ? `${fmtN(ratedCredits)} credits`
            : "Credits"
          : [
              ratedPlan ? cap(ratedPlan) : null,
              ratedCredits ? `${fmtN(ratedCredits)} credits / month` : null,
            ]
              .filter(Boolean)
              .join(" — ") || null,
        priceLabel: pausedPriceLabel,
        plan: ratedPlan,
        cadence: isRatedTopup ? null : ratedCadence,
        credits: ratedCredits,
        // Real number for the support queue, so held-up revenue can be summed
        // rather than read off a display string.
        amountUsd: pausedCents === null ? null : pausedCents / 100,
      })
    ) {
      setBusy(false)
      return
    }
    // Default: return to wherever the user opened the flow. The host page's
    // reconcile-on-return effect (keyed on ?topup / ?upgraded + session_id)
    // credits the account and refreshes the UI on arrival.
    const dest =
      returnTo ??
      (typeof window !== "undefined"
        ? window.location.pathname
        : "/billing/credits")

    // Custom Payment Element checkout (behind the flag): route each rated
    // surface to our own /checkout page — plan subscribe, raise-credits upgrade,
    // and rated top-up.
    if (isCustomCheckoutEnabled()) {
      if (url.includes("subscribe-rated") && typeof body.plan === "string") {
        redirectToBilling(
          customCheckoutHref({
            type: "rated",
            tier: String(body.plan),
            credits: body.credits as number | undefined,
            cadence: String(body.cadence ?? "monthly"),
            returnTo: dest,
          }),
        )
        return
      }
      if (url.includes("upgrade-rated")) {
        redirectToBilling(
          customCheckoutHref({
            type: "upgrade",
            credits: body.credits as number | undefined,
            returnTo: dest,
          }),
        )
        return
      }
      if (url.includes("topup-rated")) {
        redirectToBilling(
          customCheckoutHref({
            type: "topup-rated",
            credits: body.credits as number | undefined,
            returnTo: dest,
          }),
        )
        return
      }
    }
    console.log("[RATED-TOPUP] 1. client → POST", url, body)
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Forward the entry-point source + gated intent (e.g. stripe_button /
        // connect_stripe) so the origin of this upgrade is attributable server-
        // side. Extra fields are ignored by routes that don't read them.
        body: JSON.stringify({
          ...body,
          returnTo: dest,
          ...(source ? { source } : {}),
          ...(intent ? { intent } : {}),
        }),
      })
      const json = await res.json()
      console.log("[RATED-TOPUP] 2. client ← response", res.status, json)
      // Card on file → the server charged the saved card directly and granted
      // inline (no Stripe redirect). Land on the same success URL the redirect
      // flow returns to so the host reloads with fresh credits. A top-up comes
      // back on ?topup=true and a plan on ?upgraded=true — the host pages key
      // their success states on the matching flag.
      if (res.ok && json.charged) {
        // No session_id comes back on this in-app path, so the host pages'
        // reconcile-on-return effects skip it. Hand the confirmation off through
        // sessionStorage instead — the root <PurchaseConfirmationHost/> replays
        // it after the reload below (and consumes it once, so a refresh won't
        // re-show it).
        const bodyCredits =
          typeof body.credits === "number" ? body.credits : null
        const newCredits =
          typeof json.credits === "number" ? json.credits : bodyCredits
        // `dest` is the page the flow was opened from, so the CTA should dismiss
        // and leave the user exactly there (href null) — sending them to
        // /projects would undo the whole point of returning in place. Only a
        // settings destination, where there is nothing to build, routes out.
        const ctaHref = dest.startsWith("/settings") ? "/projects" : null
        if (url.includes("topup")) {
          queuePurchaseConfirmation({
            kind: "credits",
            creditsAdded: newCredits,
            source: "rated_inapp_charge",
            cta: { label: "Continue building", href: ctaHref },
          })
        } else {
          // Upgrade/subscribe on a saved card. An "Add more credits" raise keeps
          // the same tier (planId === previous → a monthly-credit increase); a
          // free→paid subscribe passes the target plan in `body`.
          const targetPlan =
            typeof body.plan === "string"
              ? (body.plan as PlanId)
              : ((status?.plan ?? "pro") as PlanId)
          // The recurring price this purchase leaves them on. Without it the
          // confirmation modal renders no price note at all — so the ONE path
          // that charges a saved card with no checkout page behind it was also
          // the one that never told the buyer their plan renews. Priced from
          // the same helpers the screens quote from, and — matching the
          // convention in resolvePurchaseConfirmation — `monthlyPriceUsd`
          // carries the YEAR's total on annual, since that is the charge.
          // `ratedPlan`, not `targetPlan`: the former is already narrowed to a
          // rated PAID tier (body.plan when subscribing, the account's own plan
          // when raising credits), while targetPlan can still be "free" — and
          // pricing "free" on the rated ladder is meaningless. Null here just
          // omits the price note, which is the correct degradation.
          const confirmCredits =
            typeof newCredits === "number" && newCredits > 0 ? newCredits : null
          const confirmPriceUsd =
            ratedPlan && confirmCredits
              ? (ratedCadence === "annual"
                  ? annualCents(ratedPlan, confirmCredits)
                  : monthlyCents(ratedPlan, confirmCredits)) / 100
              : null
          queuePurchaseConfirmation({
            kind: "plan",
            planId: targetPlan,
            previousPlanId: (status?.plan ?? null) as PlanId | null,
            monthlyCredits: newCredits,
            monthlyPriceUsd: confirmPriceUsd,
            cadence: ratedCadence,
            source: "rated_inapp_charge",
            cta: { label: "Continue building", href: ctaHref },
          })
        }
        console.log("[RATED-TOPUP] 3. charged saved card directly → refreshing", dest)
        // MERGE NOTE: dev computed its own `sep` and hard-coded `upgraded=true`
        // here, which is wrong for a top-up. completedOnSavedCard now owns the
        // navigation — it derives the separator itself and picks the correct
        // success flag — so dev's inline redirect is dropped, not its confirmation
        // queueing above (that is a real feature this path would otherwise lose:
        // a saved-card buyer would get no purchase confirmation at all).
        completedOnSavedCard(json, dest, url.includes("topup") ? "topup" : "upgraded")
        return
      }
      // ── WHERE WE SEND THEM, WHICHEVER PROVIDER ANSWERED ───────────────
      //
      // Stripe mints a hosted Checkout and returns `url`. A PayPal upgrade
      // REPLACES the subscription — a new one is minted and is sitting in
      // APPROVAL_PENDING at this point, and PayPal collects the amount due the
      // moment the payer signs for it — so the destination comes back as
      // `approval_url` instead.
      //
      // Reading only `url` reported a 200 as a failure: the customer was shown
      // "Could not start checkout" while a live pending subscription waited for
      // them on PayPal's side. Caught end-to-end on a real upgrade before this
      // line existed. The downgrade arm below already guarded the same shape;
      // this arm did not.
      //
      // Normalised into one variable rather than returned early, so the
      // departure bookkeeping underneath — the domain-upgrade marker and the
      // "what were they buying" record that reopens this modal on a cancelled
      // return — runs identically for both providers instead of being silently
      // skipped for PayPal.
      const checkoutUrl: string | undefined = json.url || json.approval_url
      if (!res.ok || !checkoutUrl) throw new Error(json.error || "Could not start checkout")
      console.log("[RATED-TOPUP] 3. redirecting to provider →", checkoutUrl)
      // We are about to leave Panda. If this upgrade was started from the domain
      // flow, record the departure so the project page can tell a real
      // cancel/browser-back return from Stripe apart from a plain revisit, and
      // restore the domain modal with the user's search results and their pick
      // (see the cancel-return effect in app/project/[id]/page.tsx). Without
      // this the whole restore is dead code: it used to be armed by the now-
      // retired UpgradeModal, and nothing on this path replaced it.
      // Only for a real redirect — a saved-card charge returns above on
      // ?upgraded=true, which the success-return effect owns.
      if (isDomainUpgradeIntent(intent)) markDomainUpgradeRedirect()
      // Record WHAT they were buying, so a return without paying (browser back,
      // Stripe's own back chevron, cancel) reopens this modal on this screen
      // with this selection instead of dumping them back to a closed paywall
      // with their plan/amount forgotten. `body` is the selection: every
      // checkout above passes `credits`, and the plan screens add `plan`.
      // Consumed by useCheckoutIntentResume in the modal hosts; a completed
      // purchase returns on ?upgraded/?topup and drops it instead.
      stashCheckoutIntent({
        surface: "rated",
        screen: screenRef.current,
        plan: typeof body.plan === "string" ? (body.plan as PaidPlanId) : undefined,
        credits: typeof body.credits === "number" ? body.credits : undefined,
        source,
        intent,
        // Browser-back lands on the page they clicked from; Stripe's cancel
        // button lands on the checkout's return URL. Those are the same page by
        // default, but a host can override `returnTo` — so honour the stash at
        // either, and nowhere else.
        paths: [window.location.pathname, dest.split("?")[0]],
      })
      redirectToBilling(checkoutUrl)
    } catch (e) {
      console.error("[RATED-TOPUP] client checkout error", e)
      setErr(e instanceof Error ? e.message : "Something went wrong")
      setBusy(false)
    }
  }

  async function lifecycle(
    url: string,
    body: Record<string, unknown>,
    // "close" → a terminal cancel/downgrade: the scheduled result now renders
    // inline on the Settings → Billing page, so close the modal (the host page
    // refreshes its billing overview on close) instead of showing an in-modal
    // banner. Any Screen value navigates within the modal as before (e.g. resume
    // → "upgrade", the add-more-credits screen). The UI only advances AFTER the
    // server confirms success.
    next: Screen | "close",
  ) {
    setErr(null)
    setBusy(true)

    // A DOWNGRADE ON WHOP IS A CHECKOUT, SO IT BELONGS ON OUR PAGE.
    //
    // Whop cannot reprice a membership in place, so it serves the change as a
    // replacement checkout — one that bills the new rate immediately, since
    // inlinePlan gives it no trial days. That is a payment screen, and it was
    // the last one reached by redirect.
    //
    // Narrowed to downgrade-rated on purpose: this helper also carries cancel,
    // pause and resume, none of which move money or produce a checkout, and all
    // of which must keep settling inline exactly as they do now.
    if (
      url.includes("downgrade-rated") &&
      isWhop() &&
      isCustomCheckoutEnabled() &&
      typeof body.plan === "string"
    ) {
      redirectToBilling(
        customCheckoutHref({
          type: "downgrade",
          plan: String(body.plan),
          returnTo:
            typeof window !== "undefined"
              ? window.location.pathname
              : "/settings/billing",
        }),
      )
      return
    }

    console.log("[RATED-DOWNGRADE] → POST", url, body)
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      console.log("[RATED-DOWNGRADE] ← response", res.status, json)
      if (!res.ok) throw new Error(json.error || "Something went wrong")
      // ── The change is not done yet: the PAYER has to approve it ────────
      // PayPal applies no subscription revision until the subscriber signs for
      // it on PayPal's site, so a 200 here means "we asked", not "it happened".
      // Treating it as success closed this modal on a customer whose plan had
      // not changed and was never going to — a silent false confirmation.
      // Send them to sign; the webhook records it when they do.
      if (typeof json.approval_url === "string" && json.approval_url) {
        window.location.href = json.approval_url
        return
      }
      if (next === "close") {
        onClose?.()
        return
      }
      setStatus((s) =>
        s
          ? {
              ...s,
              // Only the resume path passes a non-"close" screen ("upgrade");
              // reaching it means the scheduled cancel/downgrade was undone.
              cancel_scheduled: next !== "upgrade",
              period_end: json.period_end ?? s.period_end,
            }
          : s,
      )
      setScreen(next)
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong")
    } finally {
      setBusy(false)
    }
  }

  async function saveAuto(payload: {
    refill_credits: number
    threshold_credits: number
    monthly_limit_credits: number
  }) {
    if (status?.is_trial) {
      setErr(TRIAL_LOCKED_FLOW_MSG)
      return
    }
    setErr(null)
    setBusy(true)
    setAutoSaved(false)
    try {
      const res = await fetch("/api/credits/auto-topup-rated", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: true, ...payload }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || "Could not save settings")
      setAutoSaved(true)
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong")
    } finally {
      setBusy(false)
    }
  }

  // Buy screen (#4) submit. When the auto-top-up checkbox is on, persist the
  // auto-top-up setting first (best-effort, non-fatal — the checkbox doubles as
  // enabling auto top-up) and THEN start the one-off checkout. The monthly cap
  // defaults to 4× the top-up amount, matching the standalone Auto screen.
  async function buyWithAuto(
    credits: number,
    auto: { threshold: number } | null,
  ) {
    if (status?.is_trial) {
      trackTrialAddCreditsClicked({ entry_point: source, surface: "rated_flow_buy" })
      setErr(TRIAL_LOCKED_FLOW_MSG)
      return
    }
    // Gate the CTA for the WHOLE operation. The auto-top-up PUT below is a real
    // network round-trip; without setting busy first the "Buy credits" button
    // stays enabled through it (checkout() only sets busy later), so a double
    // click could fire two auto PUTs and two Stripe Checkout sessions. checkout()
    // keeps busy true on success (it navigates away) and clears it on error.
    setErr(null)
    setBusy(true)
    if (auto) {
      try {
        await fetch("/api/credits/auto-topup-rated", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled: true,
            refill_credits: credits,
            threshold_credits: auto.threshold,
            monthly_limit_credits: credits * 4,
          }),
        })
      } catch {
        /* non-fatal — still proceed to the one-off checkout */
      }
    }
    await checkout("/api/credits/topup-rated", { credits })
  }
}

// ── shared plan-card content ────────────────────────────────────────────────
// The bordered card body reused by the proactive upgrade (#1), the personalised
// out-of-credits screen (#2), and the compare-plans toggle (#5): plan name +
// "Everything in X, plus:" benefits + a monthly-credits dropdown, with the price
// at the BOTTOM of the card (per the UX proposal). Every price comes from the
// shared pricing helpers — never hardcoded.
function MostPopular() {
  return (
    <span
      className={cn(
        "rounded-full px-2.5 py-1 text-[11px] font-extrabold",
        MODAL_ACCENT_PILL,
      )}
    >
      Most popular
    </span>
  )
}

// Months of the year an annual subscriber does NOT pay for (12 − 10 = 2).
const ANNUAL_FREE_MONTHS = 12 - ANNUAL_MONTHS_CHARGED

// Billing-interval control for the PLAN screens only (choose-a-plan, compare
// plans, and the out-of-credits subscribe screen). Deliberately absent from the
// credit screens — "Add more credits" (UpgradeScreen), "Buy credits"
// (BuyScreen) and Auto top-up are credit purchases against an existing
// subscription, not a billing-interval decision, and the ticket excludes them.
// Sits on its OWN row, full width, never beside the plan tabs: the two are
// identically-shaped pill groups, so on one line they read as a single five-item
// control ("Pro Grow Scale Monthly Annual") with two competing orange selections.
// The active pill here is a neutral raised chip rather than the plan tabs' orange
// gradient, which keeps the tier the primary choice and the interval a secondary
// one. The saving rides inside the Annual pill as a green badge so the value is
// visible before it is selected.
function CadenceToggle({
  value,
  onChange,
  idPrefix,
  className,
}: {
  value: RatedCadence
  onChange: (c: RatedCadence) => void
  /** Distinguishes the radiogroup when two plan screens mount in one tree. */
  idPrefix: string
  /** Top spacing, which differs by screen (the compare view has tabs above it). */
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Billing interval"
      className={cn(
        "flex w-full gap-1 rounded-xl border border-border bg-secondary p-1",
        className,
      )}
    >
      {(["monthly", "annual"] as RatedCadence[]).map((c) => {
        const active = value === c
        return (
          <button
            key={c}
            id={`${idPrefix}-${c}`}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(c)}
            // Wraps rather than clips: on the narrowest phones the badge drops
            // to a second line instead of the saving being cut off, which is the
            // one thing this control exists to say.
            className={cn(
              "flex flex-1 flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-lg px-3 py-2 text-[13px] font-bold transition-colors",
              active
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {c === "monthly" ? "Monthly" : "Annual"}
            {c === "annual" && (
              <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] leading-none font-extrabold text-emerald-400">
                {ANNUAL_FREE_MONTHS} months free
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// Fires pricing_page_viewed for whichever plan surface is on screen, and again
// when the user steps between them (Pro card ↔ Compare plans) — each is a real
// view with its own cadence to attribute. The ref suppresses only a REPEAT of
// the surface already reported, which is what React StrictMode's double-invoked
// effect produces in dev; a genuine pro → compare → pro round trip still reports
// three views because the surface changes each time.
function usePricingSurfaceViewed(
  surface: PricingSurface,
  currentPlan: string,
  cadence: RatedCadence,
) {
  // Read through a ref so a cadence switch doesn't count as a new view — the
  // event records the interval the surface was ENTERED on. Refreshed in its own
  // effect (never during render) which, declared first, has already run by the
  // time the reporting effect below reads it on the same commit.
  const latest = React.useRef({ currentPlan, cadence })
  React.useEffect(() => {
    latest.current = { currentPlan, cadence }
  })
  const lastReported = React.useRef<PricingSurface | null>(null)
  React.useEffect(() => {
    if (lastReported.current === surface) return
    lastReported.current = surface
    trackPricingPageViewed({
      // Every rated surface is behind auth — the flow only mounts in-app.
      is_logged_in: true,
      current_plan: latest.current.currentPlan,
      surface,
      default_cadence: latest.current.cadence,
    })
  }, [surface])
}

// The terms line under a plan card. Annual states the 10-months-charged /
// 2-months-free deal explicitly so the value is visible BEFORE Stripe.
function CadenceTerms({ cadence }: { cadence: RatedCadence }) {
  return (
    <Terms>
      {cadence === "annual"
        ? `Billed annually — pay for ${ANNUAL_MONTHS_CHARGED} months and get ${ANNUAL_FREE_MONTHS} months free. `
        : "Billed monthly. "}
      {/* Not decoration on the cadence label: "Billed monthly" describes how a
          charge is sized, not that it repeats without being asked for again.
          This is the plan picker, so it is the first place the subscription
          has to announce itself as one. */}
      {cadence === "annual"
        ? "Renews automatically every year until you cancel. "
        : "Renews automatically every month until you cancel. "}
      Cancel any time in Settings &rarr; Billing. By upgrading you agree to our{" "}
      <a href="/terms" target="_blank" rel="noopener noreferrer">
        terms
      </a>
      .
    </Terms>
  )
}

function PlanCard({
  plan,
  total,
  onTotalChange,
  badge,
  selectId,
  selectLabel = "Monthly credits",
  emphasized = true,
  onSelect,
  cta,
  cadence = "monthly",
  className,
  benefits,
}: {
  plan: PaidPlanId
  total: number
  onTotalChange: (v: number) => void
  /** Overrides this plan's benefit bullets. Used by the copy A/B test to render
   *  the shared Pro feature set (lib/billing/pro-benefits.ts) — the same list
   *  the $1-trial modal shows — instead of this file's PLAN_BENEFITS. Omitted
   *  everywhere else, so every other card is untouched. */
  benefits?: { plus: string; items: readonly string[] }
  badge?: React.ReactNode
  selectId?: string
  /** Accessible name for this card's credit picker. Side-by-side cards each
   *  need their own ("Monthly credits for Pro") — a shared name would make the
   *  three pickers indistinguishable to a screen reader. */
  selectLabel?: string
  /** Whether this card is the selected/recommended one: brand ring + tint. */
  emphasized?: boolean
  /** Makes the whole card a selection target (side-by-side picker). Omitted on
   *  single-card screens, which have nothing to choose between. */
  onSelect?: () => void
  /** Per-card CTA, pinned to the bottom so CTAs align across cards of
   *  differing benefit-list length. Screens with a single card put their CTA
   *  below the card instead and leave this unset. */
  cta?: React.ReactNode
  /** Billing interval the price + footer copy render for. Defaults to monthly
   *  so any card without a toggle is unchanged. */
  cadence?: RatedCadence
  className?: string
}) {
  const ben = benefits ?? PLAN_BENEFITS[plan]
  const opts = planTotalOptions(plan)
  // Headline figure is always PER MONTH so monthly ↔ annual is comparable at a
  // glance; the annual total (what Stripe actually charges once a year) is
  // spelled out on the line beneath it.
  const price = perMonthCents(plan, total, cadence)
  const yearTotal = annualCents(plan, total)
  const selectable = Boolean(onSelect)
  return (
    <div
      data-plan={plan}
      // Selection is guidance, not a gate: it moves the ring and the primary
      // button, but every card's own button works whether or not it is
      // selected. So the card is a click target WITHOUT button/radio
      // semantics — a role here would take its accessible name from the whole
      // card (badge, benefits, price, "Upgrade to Pro") and shadow the real
      // button inside it. Keyboard users select by tabbing in: focus bubbles,
      // so reaching the picker or the button selects the card it belongs to.
      aria-current={selectable && emphasized ? "true" : undefined}
      onClick={selectable ? onSelect : undefined}
      onFocus={selectable ? onSelect : undefined}
      className={cn(
        "flex h-full flex-col rounded-2xl p-5 ring-2 transition-colors short:p-4",
        // Emerald, not the brand orange: the emphasised card is the plan we
        // are recommending, and an orange→red ring around it reads as a
        // warning about the plan rather than an endorsement of it. (PANDA-1779)
        emphasized
          ? cn("bg-emerald-600/[0.05]", MODAL_ACCENT_RING)
          : "ring-border",
        selectable && "cursor-pointer",
        selectable && !emphasized && "hover:bg-muted/40 hover:ring-emerald-600/40",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-extrabold tracking-tight">
          {cap(plan)} plan
        </h3>
        {badge}
      </div>
      <div className="mt-3.5 text-[13px] font-extrabold short:mt-2.5">{ben.plus}</div>
      <ul className="mt-3 grid gap-2.5 short:mt-2 short:gap-1.5">
        {ben.items.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-[13.5px] font-medium short:gap-2 short:text-[13px]">
            <CheckIcon className="mt-0.5 size-4 flex-none text-emerald-400" />
            {b}
          </li>
        ))}
      </ul>
      <div className="mt-auto">
        <Label htmlFor={selectId} className="mt-4 short:mt-3 short:mb-1.5">
          {selectLabel}
        </Label>
        <Select id={selectId} value={total} onChange={onTotalChange}>
          {opts.map((v) => (
            <option key={v} value={v}>
              {fmtN(v)} credits / month
            </option>
          ))}
        </Select>
        <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4 short:mt-3 short:pt-3">
          <span className="text-[13px] font-semibold text-muted-foreground">
            {cadence === "annual" ? "Billed annually" : "Billed monthly"}
          </span>
          <span className="text-[27px] font-extrabold leading-none tracking-tight short:text-[24px]">
            {usd(price)}
            <span className="text-sm font-semibold text-muted-foreground"> /mo</span>
          </span>
        </div>
        {cadence === "annual" && (
          <p className="mt-1.5 text-right text-[12.5px] font-extrabold text-emerald-400">
            {usd(yearTotal)}/year — pay for {ANNUAL_MONTHS_CHARGED} months, get{" "}
            {ANNUAL_FREE_MONTHS} free
          </p>
        )}
        {cta && <div className="mt-4 short:mt-3">{cta}</div>}
      </div>
    </div>
  )
}

// ── free / upgrade screens ──────────────────────────────────────────────────
// Out of credits, mid-session (#2). Same card as the proactive upgrade, but the
// headline + subheading are PERSONALISED from the blocked prompt (see
// /api/ai/paywall-headline), with a safe fallback when context is thin. The
// "Compare plans" link is intentionally hidden here to keep focus on resuming
// the build.
function PausedFreeScreen({
  headline,
  subheading,
  loadingHeadline,
  loadingSub,
  busy,
  currentPlan,
  initialCredits,
  onSubmit,
  copyVariant = "control",
  onDecline,
}: {
  headline: string
  subheading: string
  /** Restores the credit amount picked before a Stripe round-trip (see
   *  ChoosePlanScreen). Ignored when not a valid rung for the plan. */
  initialCredits?: number
  /** The personalised headline is still generating — show a placeholder rather
   *  than the fallback, so the real headline appears once (no flutter). */
  loadingHeadline?: boolean
  loadingSub?: boolean
  busy: boolean
  /** The account's plan, for the surface-view event only. */
  currentPlan: string
  onSubmit: (
    plan: PaidPlanId,
    credits: number,
    cadence: RatedCadence,
    surface: PricingSurface,
  ) => void
  /** Copy A/B arm. Defaults to the baseline so an unwired caller can never
   *  half-apply the rewrite. */
  copyVariant?: UpgradeCopyVariant
  /** Soft-exit handler ("Not now"). Omitted → not rendered. */
  onDecline?: () => void
}) {
  const plan: PaidPlanId = "pro"
  const rewrite = showsUpgradeCopyVariant(copyVariant)
  const [total, setTotal] = React.useState(() =>
    initialCredits != null && isValidPlanTotal(plan, initialCredits)
      ? initialCredits
      : lowestPlanTotal(plan),
  )
  // Annual is the default everywhere (product rule), and the choice is SHARED
  // with /pricing and the other plan screens and persisted — a visitor who
  // picked an interval on the pricing page sees it here, and vice versa. See
  // lib/billing/cadence-preference.
  const [cadence, setCadence] = useBillingCadence()
  usePricingSurfaceViewed("pro_pricing", currentPlan, cadence)

  function switchCadence(next: RatedCadence) {
    if (next === cadence) return
    const from = cadence
    setCadence(next)
    trackPricingToggleSwitched({
      surface: "pro_pricing",
      from_cadence: from,
      to_cadence: next,
    })
  }

  return (
    <>
      <Pill>Build paused</Pill>
      {loadingHeadline ? (
        <div
          className="space-y-2.5"
          role="status"
          aria-label="Preparing your upgrade"
        >
          <div className="h-8 w-11/12 animate-pulse rounded-lg bg-muted/40" />
          <div className="h-8 w-2/3 animate-pulse rounded-lg bg-muted/40" />
        </div>
      ) : (
        <H2>{headline}</H2>
      )}
      {loadingSub ? (
        <div className="mt-3 space-y-2" aria-hidden>
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-muted/30" />
        </div>
      ) : (
        <Sub>{subheading}</Sub>
      )}
      <CadenceToggle
        value={cadence}
        onChange={switchCadence}
        idPrefix="pfCad"
        className="mt-5 short:mt-3.5"
      />
      <PlanCard
        plan={plan}
        total={total}
        onTotalChange={setTotal}
        selectId="pfSel"
        badge={<MostPopular />}
        cadence={cadence}
        className="mt-5 short:mt-3.5"
        // Same shared Pro feature set as the choose-plan screen and the
        // $1-trial modal, so a free user meets ONE description of Pro wherever
        // they hit a wall. This is the highest-volume free paywall, so leaving
        // it on the old bullets would have left most of the traffic outside the
        // very inconsistency the test is trying to remove.
        benefits={
          rewrite
            ? { plus: "Everything in Free, plus:", items: PRO_BENEFITS_VARIANT }
            : undefined
        }
      />
      <CadenceTerms cadence={cadence} />
      <div className="mt-4 short:mt-3">
        <Button
          size="lg"
          variant="success"
          className="h-12 w-full rounded-xl text-[15px] short:h-11"
          disabled={busy}
          onClick={() => {
            trackPlanCtaClicked({ surface: "pro_pricing", plan, cadence })
            // Same primary-metric completion as ChoosePlanScreen — this screen
            // is the higher-volume free paywall, so omitting it here would
            // under-count the click side of the funnel most of all.
            trackUpgradeCtaClicked({
              entry_point: "rated_credits_modal",
              surface: "workspace",
              source: "pro_pricing",
              plan_before: currentPlan,
              plan_after: plan,
              copy_variant: copyVariant,
            })
            onSubmit(plan, total, cadence, "pro_pricing")
          }}
        >
          {busy ? "Starting…" : "Upgrade and keep building"}
        </Button>
      </div>
      {/* Soft exit — see the ChoosePlanScreen note. Wording differs because
          this user is mid-build, not plan-shopping: the honest alternative to
          upgrading here is waiting for the daily credit reset, not "staying on
          Free" (they already are). */}
      {rewrite && onDecline && (
        <Button
          variant="ghost"
          onClick={onDecline}
          disabled={busy}
          className="mt-3 h-10 w-full rounded-xl text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
        >
          Not now — I&apos;ll keep building on Free
        </Button>
      )}
    </>
  )
}

// Choose a plan — every paid plan in ONE view (PANDA merge-pricing-views).
//
// This screen used to open on a single focused card (Pro) behind a "Compare
// plans" link that swapped in a Pro/Grow/Scale toggle showing one plan at a
// time. Pricing is a high-intent moment: hiding two of the three plans behind a
// click made comparison feel like a secondary feature and put two extra steps
// between landing here and deciding. All plans now render side by side on load,
// each with its own credit picker, price and CTA, so choosing costs zero extra
// clicks. Plans come from PAID_PLAN_IDS, so a new tier appears here on its own.
//
// Emphasis is preserved rather than removed: the selected plan keeps the brand
// ring and the gradient CTA, and Pro keeps the "Most popular" badge. The billing
// interval is one choice for the whole screen — it applies to every card, so a
// user compares plans at the same cadence rather than across two of them.
function ChoosePlanScreen({
  busy,
  currentPlan,
  onSubmit,
  initialPlan,
  initialCredits,
  insufficientCredits,
  contextCopy,
  copyVariant = "control",
  onDecline,
}: {
  busy: boolean
  /** The account's plan, for the surface-view event only. */
  currentPlan: string
  /** Copy A/B arm. Defaults to the baseline so an unwired caller can never
   *  half-apply the rewrite. */
  copyVariant?: UpgradeCopyVariant
  /** Soft-exit handler ("Not now"). Omitted → the soft exit isn't rendered,
   *  since a decline button that does nothing is worse than none. */
  onDecline?: () => void
  onSubmit: (
    plan: PaidPlanId,
    credits: number,
    cadence: RatedCadence,
    surface: PricingSurface,
  ) => void
  /** Plan to emphasise (ring + primary CTA). Defaults to "pro" — the entry tier
   *  for a Pro-gated action (e.g. the Stripe/accept-payments gate). Every plan
   *  is shown either way; this only decides which one is recommended. */
  initialPlan?: PaidPlanId
  /** Credit amount the emphasised plan's picker opens on — restores the
   *  selection a user made before a Stripe round-trip they came back from.
   *  Ignored when it isn't a valid rung on that plan's ladder. */
  initialCredits?: number
  /** Positive-but-below-cost balance (PANDA-1367): shown as an honest banner
   *  above the cards instead of a "you're out of credits" screen. */
  insufficientCredits?: { available: number; required?: number } | null
  /** Gate-specific copy (e.g. a Pro-gated feature) replacing the default
   *  heading. */
  contextCopy?: RatedChoosePlanCopy
}) {
  // A legacy/unknown plan id (e.g. grandfathered "starter", never an offer
  // destination) falls back to Pro rather than emphasising a card that isn't on
  // screen.
  const recommended: PaidPlanId =
    initialPlan && PAID_PLAN_IDS.includes(initialPlan) ? initialPlan : "pro"

  const rewrite = showsUpgradeCopyVariant(copyVariant)

  // The cards are selectable: clicking one moves the brand ring and the primary
  // (gradient) button to it, so the plan you are considering is always the one
  // the screen is pointing at. Seeded to the recommended plan, so the screen
  // still opens with guidance rather than nothing chosen. Every card keeps its
  // own working button — selecting is not a step on the way to buying.
  const [selected, setSelected] = React.useState<PaidPlanId>(recommended)

  // One credit selection per plan — the ladders differ per tier, so a single
  // shared amount could not be valid on all three at once.
  const [totals, setTotals] = React.useState<Partial<Record<PaidPlanId, number>>>(
    () => {
      const seed: Partial<Record<PaidPlanId, number>> = {}
      for (const p of PAID_PLAN_IDS) seed[p] = lowestPlanTotal(p)
      if (initialCredits != null && isValidPlanTotal(recommended, initialCredits)) {
        seed[recommended] = initialCredits
      }
      return seed
    },
  )

  // Billing interval. ONE choice for the whole screen: with every plan on screen
  // at once, a per-card interval would put monthly and annual prices side by
  // side and make the comparison meaningless.
  //
  // It is annual by default and SHARED + persisted across every pricing surface
  // — /pricing and the in-app plan screens read the same preference, so the
  // interval a user picks on one is the one the next opens on (the ticket's
  // "cadence persists" requirement). This replaces the component-local state
  // this screen used to hold: that reset to monthly on every mount, which is the
  // behaviour the ticket asked us to remove, not preserve. See
  // lib/billing/cadence-preference.
  const [cadence, setCadence] = useBillingCadence()

  // Which card's CTA started the checkout. With a CTA per plan, a shared busy
  // label would put "Starting…" on all three and hide which one is being
  // bought. Only ever read while `busy` — and every submit goes through a card
  // click that sets it first — so a value left over from a failed checkout can
  // never mislabel the next one.
  const [pendingPlan, setPendingPlan] = React.useState<PaidPlanId | null>(null)

  // Every paid plan is on screen at once, so this screen IS the ticket's
  // "Compare plans" surface — the separate compare view it used to step into no
  // longer exists. The single-Pro-card paywall (PausedFreeScreen) remains the
  // "pro_pricing" surface, so both of the ticket's surfaces still report.
  const surface: PricingSurface = "compare_plans"
  usePricingSurfaceViewed(surface, currentPlan, cadence)

  function switchCadence(next: RatedCadence) {
    if (next === cadence) return
    const from = cadence
    setCadence(next)
    trackPricingToggleSwitched({
      surface,
      from_cadence: from,
      to_cadence: next,
    })
  }

  const insufficient =
    !!insufficientCredits && insufficientCredits.available > 0

  return (
    <>
      {/* Insufficient (but non-zero) balance: name available vs. required so
          the paywall never contradicts the visible counter (PANDA-1367). */}
      {insufficient && (
        <div className="mb-4 rounded-xl bg-secondary px-4 py-3 text-sm font-medium text-foreground ring-1 ring-border">
          {typeof insufficientCredits!.required === "number"
            ? `You don't have enough credits to continue building. This step needs ${insufficientCredits!.required} credits and you have ${insufficientCredits!.available} left.`
            : `You don't have enough credits to continue building. You have ${insufficientCredits!.available} left, but not enough for this step.`}
        </div>
      )}
      {/* The QA copy specified the headline "Upgrade to Pro" — written when this
          screen showed ONE Pro card behind a "Compare plans" link. It now shows
          Pro, Grow and Scale side by side, so naming only Pro would mislabel two
          of the three cards. The heading therefore stays plan-neutral and the
          variant arm takes the QA SUBHEADING, which is what actually carries the
          rewrite's substance: the concrete benefits, led by the project cap. */}
      <H2>{contextCopy?.headline ?? "Choose your plan"}</H2>
      <Sub>
        {contextCopy?.subheading ??
          (rewrite
            ? "Unlimited projects, your own domain, and payments — from your first upgrade."
            : "Everything in Free, plus more as you grow. Compare and pick the one that fits.")}
      </Sub>

      <CadenceToggle
        value={cadence}
        onChange={switchCadence}
        idPrefix="cpCad"
        className="mt-5 short:mt-3.5"
      />

      {/* Side by side from lg (where the Shell widens); stacked below it, so
          every plan stays readable on a phone without a carousel. */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3 short:mt-3 short:gap-3">
        {PAID_PLAN_IDS.map((p) => {
          const total = totals[p] ?? lowestPlanTotal(p)
          const isSelected = p === selected
          return (
            <PlanCard
              key={p}
              plan={p}
              total={total}
              onTotalChange={(v) => {
                setSelected(p)
                setTotals((prev) => ({ ...prev, [p]: v }))
              }}
              selectId={`cpSel-${p}`}
              selectLabel={`Monthly credits for ${cap(p)}`}
              emphasized={isSelected}
              onSelect={() => setSelected(p)}
              cadence={cadence}
              // Variant arm swaps ONLY Pro's bullets for the shared Pro feature
              // set, so this modal and the $1-trial modal advertise the same
              // product — the QA finding that the trial looked like a lesser
              // tier. Grow/Scale keep their "Everything in Pro, plus:" ladder,
              // which still resolves correctly because Pro remains the rung
              // beneath them; rewriting those too would break that framing.
              benefits={
                rewrite && p === "pro"
                  ? {
                      plus: "Everything in Free, plus:",
                      items: PRO_BENEFITS_VARIANT,
                    }
                  : undefined
              }
              // "Most popular" stays on Pro whichever card is selected — it is
              // a fact about the plan, not about the current selection.
              badge={p === "pro" ? <MostPopular /> : undefined}
              cta={
                <Button
                  size="lg"
                  // `success`, not `default`: `default` IS the orange→red brand
                  // gradient, so the selected plan's "Upgrade to X" was the one
                  // button on the choose-a-plan screen still painted like a
                  // warning — next to two neutral outline siblings, which made
                  // it the most alarming thing on the screen. (PANDA-1779)
                  variant={isSelected ? "success" : "outline"}
                  className="h-12 w-full rounded-xl text-[15px] short:h-11"
                  disabled={busy}
                  onClick={() => {
                    setSelected(p)
                    setPendingPlan(p)
                    // Attributed to THIS card's plan, not the emphasised one —
                    // every card buys its own plan whether or not it is
                    // selected, so the selection is not what was bought.
                    trackPlanCtaClicked({ surface, plan: p, cadence })
                    // The copy test's PRIMARY metric is
                    // upgrade_modal_shown → upgrade_cta_clicked. This modal
                    // fires the impression (rated-credits-modal) but its plan
                    // CTAs only ever fired plan_cta_clicked, so that funnel had
                    // an impression and no click and would have read 0% in both
                    // arms. Fired alongside — not instead of — plan_cta_clicked,
                    // so existing pricing dashboards are untouched.
                    trackUpgradeCtaClicked({
                      entry_point: "rated_credits_modal",
                      surface: "workspace",
                      source: surface,
                      plan_before: currentPlan,
                      plan_after: p,
                      copy_variant: copyVariant,
                    })
                    onSubmit(p, total, cadence, surface)
                  }}
                >
                  {busy && pendingPlan === p
                    ? "Starting…"
                    : `Upgrade to ${cap(p)}`}
                </Button>
              }
            />
          )
        })}
      </div>

      <CadenceTerms cadence={cadence} />

      {/* Soft exit. Before this the only ways out were "pay" and the X, which
          the QA data reads as the cause of the 3-second reflexive dismissals.
          Naming the decline makes it a choice rather than an escape. Rendered
          only when the host actually gave us somewhere to go — a decline button
          that does nothing would be worse than none. */}
      {rewrite && onDecline && (
        <Button
          variant="ghost"
          onClick={onDecline}
          disabled={busy}
          className="mt-3 h-10 w-full rounded-xl text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
        >
          Not now — stay on Free
        </Button>
      )}
    </>
  )
}

// ── paid credit screens ─────────────────────────────────────────────────────
// The old 3-way "hub" (Upgrade / Auto top-up / Buy once) is retired per the UX
// proposal: Add-more-credits now opens directly on the redesigned single-card
// UpgradeScreen (#3), which links to the Buy screen (#4). Auto top-up is folded
// into the Buy screen as a single checkbox.

// Add more credits (#3) — raise the recurring monthly allocation. Single card
// with the new-amount dropdown and a rollover benefit; one-off top-ups are
// demoted to a secondary link → BuyScreen (#4). The "cheaper per credit than
// top-ups" savings copy renders ONLY when the plan's top-up rate is genuinely
// higher than its subscription rate, so it can never make a false claim. In
// today's rated model the two rates are EQUAL, so it stays hidden until Finance
// re-bases the one-off top-up rate (ticket placeholder — see AGENTS notes).
function UpgradeScreen({
  plan, currentTotal, cadence, cadenceResolved, busy, initialCredits, onBuyOnce, onSubmit,
}: {
  plan: PaidPlanId
  /** The account's current subscribed monthly allocation (custom total), or null
   *  on a base plan. Seeds the dropdown so the modal opens on what the user is
   *  actually on — 3,000 after a 1,000→3,000 upgrade — not a hardcoded base. */
  currentTotal: number | null
  /** The subscription's existing billing interval. NOT selectable here: raising
   *  your monthly credits doesn't change how often you're billed (the ticket
   *  excludes this screen from the cadence toggle). It only drives the labels,
   *  so an annual subscriber isn't told their price is "Billed monthly". */
  cadence: RatedCadence
  /** False while `cadence` is still the seeded guess. This screen is the one
   *  that branches its whole UI on the interval, so it must render neither
   *  branch until the real value lands — see the skeleton below. */
  cadenceResolved: boolean
  busy: boolean
  /** The allocation the user had selected before a Stripe round-trip they
   *  abandoned. Wins over the current-allocation default (it IS their pick), and
   *  counts as a touch below so the re-sync can't overwrite it. Ignored when it
   *  isn't one of this account's options. */
  initialCredits?: number
  onBuyOnce: () => void
  onSubmit: (credits: number) => void
}) {
  // Options include the base ladder AND the account's current allocation (so an
  // arbitrary/grandfathered total is still selectable). The account's CURRENT
  // allocation is derived here so we can (a) default the picker to the NEXT tier
  // up, never the tier they already have, and (b) mark the current rung as such
  // and lock it out as an upgrade target (selecting it is a no-op that would
  // still charge a full month — PANDA add-credits ticket).
  const opts = upgradeTotalOptions(plan, currentTotal)
  const current = resolveUpgradeInitialTotal(plan, currentTotal)
  // The next rung above the current allocation, or null when the account is
  // already on the highest tier (→ the "top tier" state below, no picker).
  const nextUp = nextUpgradeTotal(plan, currentTotal)
  const atHighest = nextUp === null
  // A restored selection is the user's own choice, so it outranks the default —
  // but only if it's still an option AND not the current (locked) tier.
  const restored =
    initialCredits != null &&
    opts.includes(initialCredits) &&
    initialCredits !== current
      ? initialCredits
      : null
  // Default to the next tier up (the product-required nudge). `?? current` only
  // matters on the highest tier, where we render the top-tier state and never
  // read `total` anyway.
  const [total, setTotal] = React.useState(() => restored ?? nextUp ?? current)
  // The modal seeds status WITHOUT custom_monthly_credits, so currentTotal starts
  // null and the default above resolves off the base (Pro → next tier 600). Once
  // the real allocation arrives (background status fetch), re-sync the default to
  // the NEXT tier above it — but only while the user hasn't picked yet — so a
  // paid account already above 600 is nudged to its own next tier, never left
  // defaulted below its real allocation (which would downgrade it) or on the tier
  // it already has. A restored pick counts as already touched: it's a real user
  // choice, so the re-sync must leave it alone exactly as it would a fresh one.
  // Annual subscribers cannot raise their monthly allocation mid-term: the year
  // is already paid, so /api/billing/upgrade-rated refuses it (charging one
  // month for credits the drip would pay out for the whole remaining term is a
  // straight revenue leak — see the guard in that route). Show them the one-off
  // top-up, which is per-credit priced and cadence-independent, instead of a CTA
  // the server will reject.
  const annualLocked = cadence === "annual"
  const totalTouched = React.useRef(restored !== null)
  React.useEffect(() => {
    if (totalTouched.current) return
    // Default to the next tier up; on the highest tier there is none, so fall
    // back to the current allocation (the top-tier state renders instead).
    setTotal(
      nextUpgradeTotal(plan, currentTotal) ??
        resolveUpgradeInitialTotal(plan, currentTotal),
    )
  }, [plan, currentTotal])
  // Monthly only — the annual branch returned above.
  const monthly = monthlyCents(plan, total)
  // True only when subscribing really is cheaper per credit than a one-off
  // top-up for this plan. Gates the savings copy so it never lies.
  const cheaperThanTopup =
    RATED_PLANS[plan].topupRateCents > RATED_PLANS[plan].planRateCents
  const benefits = [
    ...(cheaperThanTopup ? ["Cheaper per credit than one-off top-ups"] : []),
    "Unused credits roll over, never wasted",
  ]
  // ── Interval not known yet: render NEITHER branch ────────────────────
  //
  // Everything below this point forks on `annualLocked`, and the two forks are
  // not variations of one screen — they are different offers (raise your
  // monthly allocation vs. buy a one-off top-up). Rendering either from the
  // seeded guess is how an annual subscriber got a monthly credit dropdown, a
  // $/mo price and an "Update my plan" CTA for 3–4 seconds before the modal
  // swapped it out (PANDA-2290).
  //
  // A skeleton, not a spinner and not a timeout: the wait ends when
  // /api/billing/rated-status answers, and the frame it replaces must not be
  // clickable. Deliberately placed AFTER every hook above, alongside the
  // existing `annualLocked` early return, so the hook order never changes.
  if (!cadenceResolved) {
    return (
      <>
        <H2>Add more credits</H2>
        <Sub>Checking your plan…</Sub>
        <div
          className="mt-5 animate-pulse rounded-2xl p-5 ring-1 ring-white/10"
          aria-hidden
        >
          <div className="h-6 w-2/5 rounded bg-white/10" />
          <div className="mt-4 grid gap-2.5">
            <div className="h-4 w-4/5 rounded bg-white/10" />
            <div className="h-4 w-3/5 rounded bg-white/10" />
            <div className="h-4 w-2/3 rounded bg-white/10" />
          </div>
        </div>
        <div className="mt-5 h-11 animate-pulse rounded-xl bg-white/10" aria-hidden />
        <span className="sr-only" role="status">
          Loading your billing details
        </span>
      </>
    )
  }

  if (annualLocked) {
    return (
      <>
        <H2>Add more credits</H2>
        <Sub>
          You&apos;re on annual billing, so your {fmtN(
            currentTotal ?? RATED_PLANS[plan].baseCredits,
          )}{" "}
          monthly credits are set for the rest of your year. A one-off top-up
          adds credits right away.
        </Sub>
        <div className="mt-5 rounded-2xl p-5 ring-2 ring-emerald-600">
          <h3 className="text-lg font-extrabold tracking-tight">
            {cap(plan)} plan · billed annually
          </h3>
          <ul className="mt-3.5 grid gap-2.5">
            {[
              `${fmtN(currentTotal ?? RATED_PLANS[plan].baseCredits)} credits land every month`,
              "Unused credits roll over, never wasted",
              "Change your monthly amount when your plan renews",
            ].map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-[13.5px] font-medium"
              >
                <CheckIcon className="mt-0.5 size-4 flex-none text-emerald-400" />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-5">
          <Button
            size="lg"
            variant="success"
            className="h-12 w-full rounded-xl text-[15px]"
            onClick={onBuyOnce}
          >
            Buy a one-off top-up
          </Button>
        </div>
      </>
    )
  }

  // Highest monthly tier — there's no tier above to upgrade to, so raising the
  // monthly plan isn't offered. Communicate the top-tier status clearly and
  // point to the only way to add credits now: a one-off top-up (per the ticket's
  // "already on the highest tier" acceptance criterion).
  if (atHighest) {
    return (
      <>
        <H2>Add more credits</H2>
        <Sub>
          You&apos;re on our highest monthly plan — {fmtN(current)} credits every
          month. Need more right now? A one-off top-up adds credits instantly.
        </Sub>
        <div className="mt-5 rounded-2xl p-5 ring-2 ring-emerald-600">
          <h3 className="text-lg font-extrabold tracking-tight">
            {cap(plan)} plan · highest monthly tier
          </h3>
          <ul className="mt-3.5 grid gap-2.5">
            {[
              `${fmtN(current)} credits land every month`,
              "Unused credits roll over, never wasted",
              "One-off top-ups add credits instantly, anytime",
            ].map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-[13.5px] font-medium"
              >
                <CheckIcon className="mt-0.5 size-4 flex-none text-emerald-400" />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-5">
          <Button
            size="lg"
            variant="success"
            className="h-12 w-full rounded-xl text-[15px]"
            onClick={onBuyOnce}
          >
            Buy a one-off top-up
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <H2>Add more credits</H2>
      <Sub>
        Choose your new monthly amount. Credits roll over, and you can change or
        cancel any time.
      </Sub>
      <div className="mt-5 rounded-2xl p-5 ring-2 ring-emerald-600">
        <h3 className="text-lg font-extrabold tracking-tight">
          {cap(plan)} plan
        </h3>
        <ul className="mt-3.5 grid gap-2.5">
          {benefits.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[13.5px] font-medium">
              <CheckIcon className="mt-0.5 size-4 flex-none text-emerald-400" />
              {b}
            </li>
          ))}
        </ul>
        <Label htmlFor="upSel" className="mt-4">
          Monthly credits
        </Label>
        <Select
          id="upSel"
          value={total}
          onChange={(v) => {
            totalTouched.current = true
            setTotal(v)
          }}
        >
          {opts.map((v) => (
            <option key={v} value={v} disabled={v === current}>
              {fmtN(v)} credits / month{v === current ? " (current)" : ""}
            </option>
          ))}
        </Select>
        {cheaperThanTopup && (
          <p className="mt-2.5 text-[12.5px] font-extrabold text-emerald-400">
            Best value, and cheaper per credit than one-off top-ups
          </p>
        )}
        {/* Annual returned above (annualLocked), so everything from here down is
            a monthly subscription — no cadence branching needed. */}
        <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
          <span className="text-[13px] font-semibold text-muted-foreground">
            Billed monthly
          </span>
          <span className="text-[27px] font-extrabold leading-none tracking-tight">
            {usd(monthly)}
            <span className="text-sm font-semibold text-muted-foreground"> /mo</span>
          </span>
        </div>
        {/* Matches the authoritative post-upgrade confirmation copy on the
            Settings billing card: extra credits are granted now; the new
            recurring rate applies from the next cycle. */}
        <p className="mt-1.5 text-[12px] font-medium text-muted-foreground">
          Extra credits are available right away. Your new monthly rate starts
          next billing cycle.
        </p>
      </div>
      <Terms>
        Downgrade or cancel at any time. By updating your plan you agree to our{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer">terms</a>.
      </Terms>
      <div className="mt-4">
        <Button
          size="lg"
          variant="success"
          className="h-12 w-full rounded-xl text-[15px]"
          // Guard the no-op: the current tier is a locked dropdown option, but if
          // the picker ever resolves back to it, submitting would charge a full
          // month for the plan the user already has.
          disabled={busy || total === current}
          onClick={() => onSubmit(total)}
        >
          {busy ? "Starting…" : "Update my plan"}
        </Button>
      </div>
      <button
        type="button"
        onClick={onBuyOnce}
        className="mt-3.5 block w-full text-center text-sm font-bold text-primary underline underline-offset-2"
      >
        Just need a one-off top-up?
      </button>
    </>
  )
}

// Buy once (#4) — a one-off top-up. Auto top-up is folded in as a SINGLE
// self-describing checkbox (below X, add Y automatically), replacing the
// separate Auto screen. When checked, Buy saves the auto-top-up setting (via the
// same PUT the Auto screen used) before starting the one-off checkout.
function BuyScreen({
  plan, cfg, busy, initialCredits, onBack, onSubmit,
}: {
  plan: PaidPlanId
  cfg: RatedPlan
  busy: boolean
  /** Top-up amount picked before an abandoned Stripe round-trip. Ignored when
   *  it isn't one of this plan's top-up rungs. */
  initialCredits?: number
  onBack: () => void
  onSubmit: (credits: number, auto: { threshold: number } | null) => void
}) {
  // Default the amount to 2× base (nearest top-up rung) rather than the cheapest,
  // and default auto top-up ON (product) — the user can uncheck it before buying.
  const [amount, setAmount] = React.useState(() =>
    initialCredits != null && isValidTopupAmount(plan, initialCredits)
      ? initialCredits
      : defaultTopupAmount(plan),
  )
  const [autoOn, setAutoOn] = React.useState(true)
  const [threshold, setThreshold] = React.useState<number>(100)
  const price = topupCents(plan, amount)
  return (
    <>
      <H2>Buy credits</H2>
      <Sub>A one-off top-up, no subscription.</Sub>
      <div className="mt-6">
        <Label htmlFor="bAmt">Amount</Label>
        <Select id="bAmt" value={amount} onChange={setAmount}>
          {cfg.topups.map((v) => (
            <option key={v} value={v}>
              +{fmtN(v)} credits
            </option>
          ))}
        </Select>
        <p className="mt-2 text-[12.5px] font-medium text-muted-foreground">
          Valid 12 months, no rush.
        </p>
      </div>
      <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
        <span className="text-[13px] font-semibold text-muted-foreground">One-off</span>
        <span className="text-[27px] font-extrabold leading-none tracking-tight">
          {usd(price)}
        </span>
      </div>

      {/* Auto top-up as one self-describing checkbox with an INLINE threshold
          chip inside the sentence (design #4). The spend cap is deliberately not
          shown — it is out of scope for this ticket; buyWithAuto sends a safe
          default and the backend clamps to its own monthly ceiling. The row is a
          <div> (not a <button>) so the inline <select> is valid, interactive
          markup; the box + title each toggle the checkbox. */}
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-input p-3.5">
        <button
          type="button"
          role="checkbox"
          aria-checked={autoOn}
          onClick={() => setAutoOn((v) => !v)}
          aria-label="Auto top-up when I run low"
          className={
            "mt-0.5 grid size-5 flex-none place-items-center rounded-[6px] border-[1.5px] " +
            (autoOn
              ? "border-emerald-600 bg-panda-gradient-success text-white"
              : "border-muted-foreground/50")
          }
        >
          {autoOn && <CheckIcon className="size-3.5" />}
        </button>
        <div>
          <button
            type="button"
            onClick={() => setAutoOn((v) => !v)}
            className="block text-left text-sm font-bold text-foreground"
          >
            Auto top-up when I run low
          </button>
          <p className="mt-1 text-[12.5px] font-medium text-muted-foreground">
            When your balance drops below{" "}
            <span className="relative inline-flex items-center align-middle">
              <select
                aria-label="Auto top-up threshold"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="cursor-pointer appearance-none rounded-md border border-input bg-secondary py-0.5 pr-5 pl-2 text-[12.5px] font-bold text-foreground outline-none focus:border-ring"
              >
                {RATED_AUTO_TRIGGERS.map((v) => (
                  <option key={v} value={v}>
                    {fmtN(v)} credits
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-1.5 text-[10px] text-muted-foreground">
                ⌄
              </span>
            </span>{" "}
            we&apos;ll automatically add another {fmtN(amount)} credits, so
            you&apos;re never blocked. Turn off any time.
          </p>
        </div>
      </div>

      <Terms>
        Credits expire 12 months after purchase. By continuing you agree to our{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer">terms</a>.
      </Terms>
      <Footer2>
        <Button
          variant="secondary"
          className="h-12.5 rounded-[12px]"
          onClick={onBack}
          disabled={busy}
        >
          Go back
        </Button>
        <Button
          variant="success"
          className="h-12.5 rounded-[12px]"
          onClick={() => onSubmit(amount, autoOn ? { threshold } : null)}
          disabled={busy}
        >
          {busy ? "Starting…" : "Buy credits"}
        </Button>
      </Footer2>
    </>
  )
}

function AutoScreen({
  plan, cfg, hasCard, busy, saved, onBack, onSave,
}: {
  plan: PaidPlanId
  cfg: RatedPlan
  hasCard: boolean
  busy: boolean
  saved: boolean
  onBack: () => void
  onSave: (p: { refill_credits: number; threshold_credits: number; monthly_limit_credits: number }) => void
}) {
  const [amount, setAmount] = React.useState(cfg.topups.includes(100) ? 100 : cfg.topups[0])
  const [trigger, setTrigger] = React.useState<number>(25)
  const limitOpts = autoLimitOptions(amount)
  const [limit, setLimit] = React.useState(amount * 4)
  const effLimit = limitOpts.includes(limit) ? limit : amount * 4
  const perTopup = topupCents(plan, amount)
  const count = Math.floor(effLimit / amount)
  const spend = count * perTopup

  function changeAmount(v: number) {
    setAmount(v)
    setLimit(v * 4)
  }

  return (
    <>
      <H2>Turn on auto top-up</H2>
      <Sub>Credits top up automatically when you run low.</Sub>
      {saved ? (
        <div className="mt-6 rounded-2xl bg-emerald-500/10 p-5 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-500/30">
          ✓ Auto top-up is on. +{fmtN(amount)} credits when your balance falls below {fmtN(trigger)}, up to {usd(spend)}/mo.
        </div>
      ) : (
        <>
          {!hasCard && (
            <div className="mt-4 rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-300 ring-1 ring-amber-500/30">
              Add a card on file to charge automatic top-ups.
            </div>
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="aAmt">Top-up amount</Label>
              <Select id="aAmt" value={amount} onChange={changeAmount}>
                {cfg.topups.map((v) => <option key={v} value={v}>{fmtN(v)} credits</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="aTrig">When my balance falls below</Label>
              <Select id="aTrig" value={trigger} onChange={setTrigger}>
                {RATED_AUTO_TRIGGERS.map((v) => <option key={v} value={v}>{fmtN(v)} credits</option>)}
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-baseline justify-between gap-3">
              <Label htmlFor="aLim">Monthly spend limit</Label>
              <span className="text-[13px] font-semibold text-muted-foreground">{count} top-ups · {usd(spend)}</span>
            </div>
            <Select id="aLim" value={effLimit} onChange={setLimit}>
              {limitOpts.map((v) => <option key={v} value={v}>{fmtN(v)} credits</option>)}
            </Select>
            <p className="mt-2 text-[13px] font-medium text-muted-foreground">
              Cycle spend unavailable
            </p>
          </div>
          <BigPrice value={usd(perTopup)} unit="per top-up" />
        </>
      )}
      <Terms>Credits expire 12 months after purchase. Turn off auto top-up at any time. By continuing you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer">terms</a>.</Terms>
      <Footer2>
        <Button variant="secondary" className="h-12.5 rounded-xl" onClick={onBack} disabled={busy}>
          {saved ? "Done" : "Go back"}
        </Button>
        {!saved && (
          <Button
            variant="success"
            className="h-12.5 rounded-xl"
            disabled={busy || effLimit <= trigger || amount <= trigger}
            onClick={() => onSave({ refill_credits: amount, threshold_credits: trigger, monthly_limit_credits: effLimit })}
          >
            {busy ? "Saving…" : "Enable auto top-up"}
          </Button>
        )}
      </Footer2>
    </>
  )
}

// ── cancel / downgrade screens ──────────────────────────────────────────────
function CancelConfirmScreen({
  plan, monthlyCredits, periodEnd, isTrial, busy, onLite, onKeep, onCancel,
}: {
  plan: PaidPlanId
  /** The account's current monthly plan allowance (custom total, or the plan's
   *  plan base) — shown dropping to 0 in the consequences list. */
  monthlyCredits: number
  periodEnd: string | null
  /** The subscription is still a live $1 trial → the whole confirm step reads
   *  as a TRIAL cancellation, never a paid Pro cancellation (PANDA trial-copy). */
  isTrial: boolean
  busy: boolean
  onLite: () => void
  onKeep?: () => void
  onCancel: () => void
}) {
  // Sentence-case only the plan name (the id is lowercase, e.g. "pro"); the rest
  // of the line stays sentence-cased — no `capitalize` class, which would
  // title-case every word ("You'll Keep Pro Access Until …").
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1)
  // A trial never became a paid subscription, so its consequences read
  // differently: it won't convert to Pro, there's no $29/mo charge, and the
  // balance is trial credits (not a monthly allowance dropping to 0).
  const items = isTrial
    ? [
        `Your trial ends on ${fmtDate(periodEnd)}`,
        "You'll move to the Free plan",
        "Your trial won't convert to Pro — you won't be charged",
        "Custom domains & Autonomous Grow Mode turn off",
        "Unused trial credits expire when your trial ends",
      ]
    : [
        `Your subscription ends on ${fmtDate(periodEnd)}`,
        "You'll move to the Free plan",
        `Monthly credits drop from ${fmtN(monthlyCredits)} to 0 (daily credits only)`,
        "Custom domains & Autonomous Grow Mode turn off",
        "Unused credits expire when your plan ends",
      ]
  return (
    <>
      <Logo />
      <H2>{isTrial ? "Cancel your trial?" : "Cancel your subscription?"}</H2>
      <Sub>Keep your momentum — you can come back any time.</Sub>
      <Panel>
        <div className="text-sm font-extrabold">Here&apos;s what happens if you cancel:</div>
        <ul className="mt-4 grid gap-4">
          {items.map((t) => (
            <li key={t} className="flex items-start gap-3 text-[15px] font-medium">
              <XIcon className="mt-0.5 size-4 flex-none text-muted-foreground" />{t}
            </li>
          ))}
        </ul>
        {/* The cancel action lives INSIDE this sentence rather than in the
            footer: "Keep my plan" stays the only button-styled control on the
            screen, so cancelling reads as a link in the details copy rather
            than a competing CTA.

            It is underlined AT REST (not hover-only). That is a deliberate
            reversal of the earlier hover-only treatment, which failed WCAG
            1.4.1 — with no resting affordance the control was
            indistinguishable from the surrounding prose until pointed at, and
            not discoverable at all without a pointer. De-emphasis now comes
            from placement, size and the muted colour it inherits; the
            underline is what marks it as operable. Hover still adds the
            foreground colour.

            Two things about it are NOT negotiable:

              • It stays a <button>, never an <a>. It performs an action rather
                than navigating, and an <a> without href is not focusable at
                all — that would put cancelling out of reach of keyboard and
                screen-reader users entirely. A button is valid phrasing
                content inside a <p>, so this nests legally.
              • The default focus ring is left intact (no outline-none). Tab
                still lands on it visibly.

            Behaviour is unchanged: this opens the second confirm screen and
            never cancels on its own. */}
        <p className="mt-4.5 text-sm font-medium text-muted-foreground">
          {isTrial
            ? `You'll keep trial access until ${fmtDate(periodEnd)}.`
            : `You'll keep ${planLabel} access until ${fmtDate(periodEnd)}.`}{" "}
          If you&apos;d rather not continue, you can{" "}
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="underline underline-offset-2 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isTrial ? "cancel your trial" : "cancel your subscription"}
          </button>
          .
        </p>
        {/* Only advertise the Lite re-entry when Lite can actually be switched
            to — otherwise this link presents an unavailable action. */}
        {LITE_AVAILABLE && (
          <button type="button" onClick={onLite} className="mt-3.5 text-sm font-bold text-primary underline underline-offset-2">
            Not ready to leave? Switch to Lite for {usd(LITE.price * 100)}/mo
          </button>
        )}

      </Panel>
      {/* One button now, so the 2-col Footer2 grid would leave a hole — this is
          a single full-width affirmative. The destructive action moved into the
          panel above (see the note there). */}
      <div className="mt-6">
        <Button
          variant="success"
          className="h-12.5 w-full rounded-[12px]"
          onClick={onKeep}
          disabled={busy}
        >
          Keep my plan
        </Button>
      </div>
    </>
  )
}

// Second, final confirmation before a cancel actually runs. Reached only from
// CancelConfirmScreen's cancel link — this is the ONLY screen that fires the
// real cancel request.
function CancelFinalConfirmScreen({
  periodEnd, isTrial, busy, onKeep, onConfirm,
}: {
  periodEnd: string | null
  /** Live $1 trial → trial-worded final confirm, not paid subscription copy. */
  isTrial: boolean
  busy: boolean
  /** Keep the plan and leave — the SAME action as the first screen's
   *  "Keep my plan" (the host's onClose), not a step back into the flow. The
   *  subscription is untouched either way; this simply ends the journey where
   *  the user chose to stay. */
  onKeep?: () => void
  onConfirm: () => void
}) {
  return (
    <>
      <Logo />
      <H2>
        {isTrial
          ? "Are you sure you want to cancel your trial?"
          : "Are you sure you want to cancel?"}
      </H2>
      <Sub>
        {isTrial ? "Your trial" : "Your subscription"} will end on{" "}
        {fmtDate(periodEnd)}{" "}and you&apos;ll move to the Free plan.
      </Sub>
      {/* The destructive confirm is a text link, not a red button — same shape
          as the first screen, so "Keep my plan" is the only button-styled
          control on either step and the two screens read as one decision.

          It keeps the affirmative wording it had as a button ("Yes, cancel my
          trial"), because this is the click that actually cancels: the label
          has to answer the heading's question, not describe a navigation. Same
          non-negotiables as the first screen's link — a real <button> (never a
          bare <a>), an underline at rest so it is identifiable as operable
          without hovering, and the default focus ring left intact. */}
      <p className="mt-6 text-center text-sm font-medium text-muted-foreground">
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="underline underline-offset-2 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy
            ? "Scheduling…"
            : isTrial
              ? "Yes, cancel my trial"
              : "Yes, cancel my subscription"}
        </button>
      </p>
      {/* Single full-width affirmative, matching the first screen's footer. */}
      <div className="mt-4">
        <Button
          variant="success"
          className="h-12.5 w-full rounded-[12px]"
          onClick={onKeep}
          disabled={busy}
        >
          Keep my plan
        </Button>
      </div>
    </>
  )
}

function LiteScreen({
  origin, available, onSwitchLite, onSecondary,
}: {
  /** How the user reached this shared offer. Drives ONLY the secondary CTA —
   *  the primary "Switch to Lite" is identical for both origins. */
  origin: "cancel" | "downgrade"
  /** Whether Lite can actually be switched to. When false the primary CTA is
   *  shown DISABLED with customer-facing "not available yet" copy and
   *  onSwitchLite is never called — we never present an unavailable plan as an
   *  enabled action, and never surface internal rollout status. */
  available: boolean
  /** The real plan-change action — only wired/called when `available`. */
  onSwitchLite?: () => void
  onSecondary: () => void
}) {
  const secondaryLabel =
    origin === "cancel" ? "Cancel subscription" : "Downgrade plan"
  return (
    <>
      <Logo />
      <H2>Switch to Lite</H2>
      <Sub>Try our new Lite plan and retain important features that keep your project going.</Sub>
      <Panel>
        <h3 className="text-lg font-extrabold tracking-tight">Lite plan</h3>
        <div className="mt-3 mb-4 flex flex-wrap items-center gap-3">
          <BigPrice value={usd(LITE.price * 100)} unit="per month" />
          <span className="rounded-lg bg-emerald-500/10 px-3 py-1.5 text-sm font-bold text-emerald-400">{LITE.off}</span>
        </div>
        <ul className="grid gap-4">
          {LITE.keeps.map((k) => (
            <li key={k} className="flex items-start gap-3 text-[15px] font-medium">
              <CheckIcon className="mt-0.5 size-4 flex-none text-foreground" />{k}
            </li>
          ))}
        </ul>
        {/* Enabled ONLY when Lite is available AND a real handler is wired —
            so flipping LITE_AVAILABLE true without also passing onSwitchLite
            can never produce an enabled CTA that does nothing (criterion B). */}
        <Button
            variant="success"
          className="mt-5 h-12 w-full"
          onClick={onSwitchLite}
          disabled={!available || !onSwitchLite}
        >
          {available ? "Switch to Lite" : "Lite is not available yet"}
        </Button>
      </Panel>
      {/* When Lite is unavailable, the secondary CTA is the user's real way
          forward — reassure them the original cancel/downgrade still works. */}
      {!available && (
        <p className="mt-3 text-center text-sm font-medium text-muted-foreground">
          You can still continue with your {origin === "cancel" ? "cancellation" : "downgrade"} below.
        </p>
      )}
      <div className="mt-4">
        <Button variant="outline" className="h-12 w-full" onClick={onSecondary}>{secondaryLabel}</Button>
      </div>
    </>
  )
}

// Lower-paid-tier picker — the FIRST step of a downgrade (PANDA-1411). Lists the
// eligible cheaper paid plans (Scale → Grow/Pro, Grow → Pro), never Free. This
// is what makes "Downgrade" offer lower PAID plans instead of masquerading as a
// cancellation. If no lower paid tier exists (Pro — the lowest), it says so and
// points the user at cancellation, though the menu already hides Downgrade there.
function ChooseDowngradeScreen({
  plan, currentCredits, selected, onSelect, onKeep, onContinue,
}: {
  plan: PaidPlanId
  currentCredits: number
  selected: PaidPlanId | null
  onSelect: (p: PaidPlanId) => void
  onKeep?: () => void
  onContinue: () => void
}) {
  const options = lowerPaidPlanIds(plan)
  return (
    <>
      <Logo />
      <H2>Choose a lower plan</H2>
      <Sub>
        Move to a cheaper paid plan and keep building. You stay on{" "}
        {cap(plan)} ({fmtN(currentCredits)} credits) until the end of your
        billing cycle.
      </Sub>
      {options.length === 0 ? (
        <>
          <Panel>
            <p className="text-[15px] font-medium">
              You&apos;re already on our lowest paid plan. To reduce further you
              can cancel your subscription instead.
            </p>
          </Panel>
          <div className="mt-4">
            <Button
              variant="success"
              className="h-12 w-full"
              onClick={onKeep}
            >
              Keep current plan
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-5 grid gap-3">
            {options.map((p) => {
              const cfg = RATED_PLANS[p]
              const price = monthlyCents(p, cfg.baseCredits)
              const active = selected === p
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onSelect(p)}
                  aria-pressed={active}
                  aria-label={`Downgrade to ${cap(p)}`}
                  className={
                    "flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors " +
                    (active
                      ? "border-primary ring-1 ring-primary"
                      : "border-border hover:bg-muted/40")
                  }
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={
                        "grid size-5 place-items-center rounded-full border-[1.5px] " +
                        (active ? "border-primary" : "border-muted-foreground/50")
                      }
                    >
                      {active && (
                        <span className="size-2.5 rounded-full bg-primary" />
                      )}
                    </span>
                    <div>
                      <div className="text-[15px] font-bold capitalize">
                        {p} plan
                      </div>
                      <div className="mt-0.5 text-[13px] font-medium text-muted-foreground">
                        {fmtN(cfg.baseCredits)} credits / month
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[15px] font-extrabold">{usd(price)}</div>
                    <div className="text-[12px] font-medium text-muted-foreground">
                      per month
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
          <div className="mt-6 grid gap-3">
            {/* Neutral, not green: this advances a downgrade. Green would
                cheer the user toward cutting their plan, which is the dark
                pattern the PANDA-1779 policy calls out. It is not brand
                orange either — that read as a warning. */}
            <Button
              variant="secondary"
              className="h-12"
              onClick={onContinue}
              disabled={!selected}
            >
              Continue
            </Button>
            <Button variant="outline" className="h-12" onClick={onKeep}>
              Keep current plan
            </Button>
          </div>
        </>
      )}
    </>
  )
}

// Downgrade confirmation — names the SELECTED target plan and the effective
// date explicitly (acceptance criteria), and states it's scheduled (nothing
// changes today). Fires the paid-tier downgrade, distinct from cancellation.
function DowngradeConfirmScreen({
  fromPlan, fromCredits, toPlan, periodEnd, busy, onBack, onConfirm,
}: {
  fromPlan: PaidPlanId
  fromCredits: number
  toPlan: PaidPlanId
  periodEnd: string | null
  busy: boolean
  onBack: () => void
  onConfirm: () => void
}) {
  const toCfg = RATED_PLANS[toPlan]
  const newPrice = monthlyCents(toPlan, toCfg.baseCredits)
  const dateLabel = fmtDate(periodEnd)
  const items = [
    `Your plan switches to ${cap(toPlan)} on ${dateLabel}`,
    `New price ${usd(newPrice)}/month, starting your next billing cycle`,
    `Monthly credits change from ${fmtN(fromCredits)} to ${fmtN(toCfg.baseCredits)}`,
    `You keep ${cap(fromPlan)} and your current credits until ${dateLabel}`,
  ]
  return (
    <>
      <Logo />
      <H2>Downgrade to {cap(toPlan)}?</H2>
      <Sub>
        This is scheduled, not immediate — nothing changes today. You can undo it
        any time before {dateLabel}.
      </Sub>
      <Panel>
        <div className="text-sm font-extrabold">Here&apos;s what will change:</div>
        <ul className="mt-4 grid gap-4">
          {items.map((t) => (
            <li
              key={t}
              className="flex items-start gap-3 text-[15px] font-medium"
            >
              <InfoIcon className="mt-0.5 size-4 flex-none text-muted-foreground" />
              {t}
            </li>
          ))}
        </ul>
      </Panel>
      <Footer2>
        <Button
          variant="secondary"
          className="h-12"
          onClick={onBack}
          disabled={busy}
        >
          Go back
        </Button>
        <Button
          variant="secondary"
          className="h-12"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Scheduling…" : `Downgrade to ${cap(toPlan)}`}
        </Button>
      </Footer2>
    </>
  )
}

function ScheduledBanner({
  tone, title, desc, action, busy, onAction,
}: {
  tone: "warn" | "red"
  title: string
  desc: string
  action: string
  busy: boolean
  onAction: () => void
}) {
  const toneCls =
    tone === "warn"
      ? "bg-amber-500/10 ring-amber-500/30 text-amber-300"
      : "bg-destructive/10 ring-destructive/30 text-destructive"
  return (
    <>
      <H2>Plans &amp; credit usage</H2>
      <Sub>Manage your subscription plan and credit balance.</Sub>
      <div className={"mt-6 flex flex-col items-start justify-between gap-4 rounded-2xl px-5 py-4.5 ring-1 sm:flex-row sm:items-center " + toneCls}>
        <div className="flex gap-3">
          <InfoIcon className="mt-0.5 size-4.5 flex-none" />
          <div>
            <div className="text-[15px] font-extrabold">{title}</div>
            <div className="mt-1 text-sm font-semibold opacity-80">{desc}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onAction}
          disabled={busy}
          className="rounded-lg bg-foreground px-4 py-2.5 text-sm font-bold whitespace-nowrap text-background disabled:opacity-50"
        >
          {busy ? "Working…" : action}
        </button>
      </div>
    </>
  )
}

// ── shared primitives ────────────────────────────────────────────────────
function Shell({
  children,
  onClose,
  fullScreenOnMobile,
  wide,
}: {
  children: React.ReactNode
  onClose?: () => void
  fullScreenOnMobile?: boolean
  /** Choose-a-plan shows every plan side by side, which needs more room than
   *  the single-card screens. `mx-auto` keeps the narrow screens centred inside
   *  the (now wider) modal popup. */
  wide?: boolean
}) {
  return (
    <div
      className={cn(
        "relative mx-auto flex w-full max-w-140 flex-col overflow-hidden rounded-3xl bg-card text-card-foreground shadow-2xl ring-1 ring-foreground/10",
        // Never taller than whatever bounds us. Inside the modal that is the
        // popup's dynamic-viewport cap; on the standalone page usages
        // (rated-credits-screen / -review) the parent has no definite height, so
        // this resolves to `none` and the card stays content-height as before.
        "max-h-full",
        // Phones: fill the whole screen (edge-to-edge, no rounding/ring/shadow)
        // so the flow reads as a full page. From sm up the classes above win
        // again (via the sm: overrides here) → the unchanged centered card.
        fullScreenOnMobile &&
          "h-full max-w-none rounded-none shadow-none ring-0 sm:h-auto sm:max-w-140 sm:rounded-3xl sm:shadow-2xl sm:ring-1 sm:ring-foreground/10",
        // Wider only from lg, where three cards fit; below that they stack and
        // the standard width still reads best.
        wide && "lg:max-w-4xl",
      )}
    >
      {/* Pinned, NOT scrolled. It sits on the card rather than inside the
          scrolling body below, so it stays reachable at every scroll position —
          previously it was absolute inside the scroller and slid off the top the
          moment the user scrolled down to reach a CTA. (PANDA-1565) */}
      {onClose && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="absolute top-4 right-4 z-20 rounded-full bg-card/80 text-foreground/60 backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <XIcon className="size-5" />
        </Button>
      )}
      {/* The one scroll region. Padding lives here (not on the card) so the
          scrollbar runs the card's full height instead of floating inside the
          padding box. Tightened on short viewports so the common laptop case
          fits with no scroll at all. */}
      <div
        className={cn(
          MODAL_SCROLL_BODY,
          "p-7 short:p-5 sm:p-8 sm:short:p-6",
          // Full-bleed phone sheet only needs a tighter base inset; the short:
          // and sm: steps above still apply.
          fullScreenOnMobile && "p-6",
        )}
      >
        {children}
      </div>
    </div>
  )
}
const Notice = ({ children }: { children: React.ReactNode }) => (
  <p className="py-6 text-center text-sm font-medium text-muted-foreground">{children}</p>
)
// The `short:` steps below trade a little display size for fit: on a short
// viewport they buy back roughly 60px of height across the header alone, which
// is often the difference between the CTA sitting on screen and sitting under
// the fold. Full-size on anything taller. (PANDA-1565)
const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-3xl leading-tight font-extrabold tracking-tight short:text-[26px]">{children}</h2>
)
const Sub = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-2.5 text-[15px] font-medium text-muted-foreground short:mt-2 short:text-[14px]">{children}</p>
)
// `.bg-panda-brand-mark`, not `.bg-panda-gradient`: identical paint, but this
// is the Panda mark rather than a CTA, and the PANDA-1779 guard treats the two
// names differently. Every actual button in this flow is green.
const Logo = () => <div className="mb-4 size-10 rounded-xl bg-panda-brand-mark" aria-hidden />
const Pill = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-extrabold text-emerald-400">
    <span className="size-1.5 rounded-full bg-emerald-400" />
    {children}
  </div>
)
const Panel = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-5 rounded-2xl p-5 ring-1 ring-foreground/10">{children}</div>
)
const Footer2 = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-6 grid gap-3.5 sm:grid-cols-2">{children}</div>
)
const Label = ({
  children,
  htmlFor,
  className = "",
}: {
  children: React.ReactNode
  htmlFor?: string
  className?: string
}) => (
  <label
    htmlFor={htmlFor}
    className={`mb-2 block text-sm font-semibold text-foreground ${className}`}
  >
    {children}
  </label>
)
const Terms = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-5 text-[13px] leading-relaxed font-medium text-muted-foreground/80 short:mt-3.5 short:leading-snug *:[a]:underline *:[a]:underline-offset-2">{children}</p>
)
const BigPrice = ({ value, unit }: { value: string; unit: string }) => (
  <div className="mt-4 mb-4 flex items-baseline gap-2">
    <span className="text-[44px] leading-none font-extrabold tracking-tight">{value}</span>
    <span className="text-base font-semibold text-muted-foreground">{unit}</span>
  </div>
)
function Select({
  value, onChange, children, id,
}: {
  value: number
  onChange: (v: number) => void
  children: React.ReactNode
  id?: string
}) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-input bg-secondary px-3.5 pr-9 text-sm font-medium text-foreground outline-none focus:border-ring short:h-10"
      >
        {children}
      </select>
      <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground">⌄</span>
    </div>
  )
}
